import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../components/ui/drawer';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { toast } from 'sonner';
import { StyleDetectionTab } from '../components/StyleDetectionTab';
import { CreateOpportunityModal } from '../components/opportunities/CreateOpportunityModal';
import { SuggestedSignalsDraftSection } from '../components/intelligence/SuggestedSignalsDraftSection';
import { DiscoverProspectsPanel } from '../components/prospects/DiscoverProspectsPanel';
import { MyListsView } from '../components/prospects/MyListsView';
import { 
  Radar, 
  Users, 
  Map, 
  BarChart3, 
  Plus, 
  ExternalLink, 
  Building2, 
  MapPin, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  X,
  Loader2,
  Search,
  Globe,
  Briefcase,
  Target,
  Eye,
  DollarSign,
  Settings,
  Zap,
  UserPlus,
  Sparkles
} from 'lucide-react';
import { APIProvider, Map as GoogleMap, AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const SIGNAL_PROMPTS = {
  new_opening: "New location opening announced",
  new_hotel: "New hotel opening announced",
  renovation: "Property renovation underway",
  rebrand: "Brand conversion planned",
  ownership_change: "Change in property ownership",
  expansion: "Property expansion announced",
  hiring_surge: "Large hiring activity detected",
  new_gm: "New general manager appointment",
  construction: "Construction project underway",
  sales_hiring: "Sales hiring activity",
  territory_expansion: "Territory expansion planned",
  branch_opening: "Branch opening announced",
  acquisition: "Acquisition activity detected",
  development_permits: "Development permits filed",
  new_brand: "New brand entering market",
  market_growth: "Market growth activity detected",
  competitor_exit: "Competitor exiting market",
  franchise_activity: "Franchise expansion activity"
};

// Industry configuration
const INDUSTRY_CONFIG = {
  hotels: { label: "Hotels", icon: "🏨", color: "#3B82F6", bgColor: "bg-blue-100 text-blue-700" },
  restaurants: { label: "Restaurants", icon: "🍽️", color: "#F59E0B", bgColor: "bg-amber-100 text-amber-700" },
  healthcare: { label: "Healthcare", icon: "🏥", color: "#10B981", bgColor: "bg-emerald-100 text-emerald-700" },
  retail: { label: "Retail", icon: "🛍️", color: "#8B5CF6", bgColor: "bg-purple-100 text-purple-700" }
};

// Revenue potential configuration
const REVENUE_POTENTIAL_CONFIG = {
  enterprise: { label: "Enterprise", color: "bg-purple-100 text-purple-700 border-purple-200" },
  high: { label: "High", color: "bg-green-100 text-green-700 border-green-200" },
  medium: { label: "Medium", color: "bg-amber-100 text-amber-700 border-amber-200" },
  low: { label: "Low", color: "bg-gray-100 text-gray-600 border-gray-200" }
};

const SIGNAL_STRENGTH_COLORS = {
  low: "bg-gray-100 text-gray-700 border-gray-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  high: "bg-red-100 text-red-700 border-red-200"
};

const CONFIDENCE_COLORS = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-emerald-100 text-emerald-700"
};

const MARKET_SEGMENTS = {
  luxury: "Luxury & Premium",
  upscale: "Upscale",
  midscale: "Midscale",
  economy: "Economy",
  extended_stay: "Extended Stay",
  boutique: "Boutique",
  resort: "Resort",
  convention: "Convention & Conference",
  uniform_workwear: "Uniform & Workwear Providers",
  facility_services: "Facility Services Companies",
  industrial_laundry: "Industrial Laundry Services",
  corporate_apparel: "Corporate Apparel Providers",
  hospitality_uniform: "Hospitality Uniform Suppliers"
};

function getScoreColor(score) {
  if (score >= 70) return "text-green-600 bg-green-100";
  if (score >= 40) return "text-amber-600 bg-amber-100";
  return "text-red-600 bg-red-100";
}

function getScoreLabel(score) {
  if (score >= 70) return "High";
  if (score >= 40) return "Medium";
  return "Low";
}

const INDUSTRIES = [
  "Hospitality",
  "Corporate",
  "Healthcare",
  "Education",
  "Retail",
  "Restaurant",
  "Entertainment",
  "Other"
];

const DEPARTMENTS = [
  { value: "procurement", label: "Procurement" },
  { value: "hr", label: "Human Resources" },
  { value: "operations", label: "Operations" },
  { value: "gm", label: "General Manager" },
  { value: "f_and_b", label: "Food & Beverage" },
  { value: "events", label: "Events / Banquets" },
  { value: "executive", label: "Executive Leadership" }
];

// ===================== RADAR TAB =====================

function RadarTab({ token, highlightedSignalId, viewingAsUser, sellerProfile }) {
  const api = useApi();
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [signalCategory, setSignalCategory] = useState('property');
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertSignalId, setConvertSignalId] = useState(null);
  const [convertCompanyName, setConvertCompanyName] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [expandedSignalId, setExpandedSignalId] = useState(highlightedSignalId || null);
  // NEW: State for company conversion dialog (additive - does not replace existing)
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companySignalId, setCompanySignalId] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [convertingCompany, setConvertingCompany] = useState(false);
  // NEW: State for smart discovery flow
  const [discoveryDialogOpen, setDiscoveryDialogOpen] = useState(false);
  const [discoverySignal, setDiscoverySignal] = useState(null);
  const [discoveredCompanies, setDiscoveredCompanies] = useState([]);
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState(0);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [enrichmentData, setEnrichmentData] = useState(null);
  const [discoveryStep, setDiscoveryStep] = useState('discover'); // 'discover', 'select', 'enrich', 'confirm'
  const navigate = useNavigate();
  // NEW: State for enhanced Create Opportunity modal
  const [createOppModalOpen, setCreateOppModalOpen] = useState(false);
  const [signalForCreate, setSignalForCreate] = useState(null);
  // Feb 2026 — Market Segment dropdown is now driven by the seller's
  // Industry Architect profile (`target_segments`). Falls back to the
  // legacy hospitality list ONLY if the profile hasn't been filled in.
  const [sellerTargetSegments, setSellerTargetSegments] = useState([]);
  const [formData, setFormData] = useState({
    company_name: '',
    city: '',
    province_state: '',
    country: '',
    signal_type: '',
    description: '',
    source_link: '',
    signal_strength: 'medium',
    signal_confidence: 'medium',
    market_segment: '',
    location_scope: 'city'  // 'city' or 'country'
  });

  useEffect(function() { fetchSignals(); }, [filterCategory, viewingAsUser?.id]);

  // Load the seller's configured target segments once so the "Add Signal"
  // modal's Market Segment dropdown reflects what they actually sell to.
  useEffect(function() {
    async function loadSellerSegments() {
      try {
        const data = await api.get('/sales-intelligence/seller-context');
        const segs = (data && data.profile && data.profile.target_segments) || [];
        setSellerTargetSegments(
          Array.isArray(segs) ? segs.filter(function(s) { return s && String(s).trim(); }) : []
        );
      } catch (_err) {
        // Endpoint missing / user hasn't configured yet — leave empty so
        // the modal falls back to the legacy hardcoded list.
        setSellerTargetSegments([]);
      }
    }
    loadSellerSegments();
  }, [viewingAsUser?.id]);

  // Auto-expand and scroll to highlighted signal
  useEffect(function() {
    if (highlightedSignalId && signals.length > 0) {
      setExpandedSignalId(highlightedSignalId);
      // Scroll to the signal after a brief delay
      setTimeout(() => {
        const element = document.getElementById(`signal-${highlightedSignalId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [highlightedSignalId, signals]);

  async function fetchSignals() {
    setLoading(true);
    try {
      let endpoint = `/market-intelligence/signals?limit=500`;
      if (filterCategory !== 'all') {
        endpoint += `&signal_category=${filterCategory}`;
      }
      const data = await api.get(endpoint);
      setSignals(data || []);
    } catch (err) {
      console.error('Error fetching signals:', err);
    } finally {
      setLoading(false);
    }
  }

  function handlePromptSelect(type) {
    setSelectedPrompt(type);
    setFormData(prev => ({
      ...prev,
      signal_type: type,
      description: SIGNAL_PROMPTS[type]
    }));
  }

  async function handleCreateSignal() {
    if (signalCategory === 'property' && !formData.company_name) {
      toast.error('Property signals require a company name');
      return;
    }
    if (!formData.signal_type) {
      toast.error('Please select a signal type');
      return;
    }

    try {
      await api.post('/market-intelligence/signals', {
        signal_category: signalCategory,
        company_name: signalCategory === 'property' ? formData.company_name : null,
        location: {
          city: formData.city,
          province_state: formData.province_state,
          country: formData.country
        },
        signal_type: formData.signal_type,
        description: formData.description,
        source_link: formData.source_link,
        signal_strength: formData.signal_strength,
        signal_confidence: formData.signal_confidence,
        market_segment: formData.market_segment || null
      });

      toast.success(`${signalCategory === 'property' ? 'Property' : 'Market'} signal created`);
      setDialogOpen(false);
      resetForm();
      fetchSignals();
    } catch (err) {
      toast.error('Error creating signal');
    }
  }

  async function handleDismiss(signalId) {
    try {
      await api.post(`/market-intelligence/signals/${signalId}/dismiss`);
      fetchSignals();
      toast.success('Signal dismissed');
    } catch (err) {
      toast.error('Error dismissing signal');
    }
  }

  async function handleConvert(signalId) {
    try {
      await api.post(`/market-intelligence/signals/${signalId}/convert`);
      toast.success('Opportunity created successfully');
      fetchSignals();
    } catch (err) {
      toast.error('Error converting signal');
    }
  }

  async function handleConvertToProperty() {
    if (!convertCompanyName.trim()) {
      toast.error('Please enter a company name');
      return;
    }
    try {
      // Step 1: Convert to property signal (sets company name)
      await api.post(`/market-intelligence/signals/${convertSignalId}/convert-to-property?company_name=${encodeURIComponent(convertCompanyName)}`);
      
      // Step 2: Automatically create opportunity from the converted signal
      try {
        await api.post(`/market-intelligence/signals/${convertSignalId}/convert`);
        toast.success('Property added to pipeline!');
      } catch (oppErr) {
        toast.error(oppErr.message || 'Converted to property but failed to create opportunity');
      }
      setConvertDialogOpen(false);
      setConvertCompanyName('');
      setConvertSignalId(null);
      fetchSignals();
    } catch (err) {
      toast.error(err.message || 'Error converting signal');
    }
  }

  // ==================== NEW COMPANY CONVERSION HANDLERS (ADDITIVE) ====================
  // These handlers ADD company conversion capability WITHOUT modifying existing property flows
  
  // Signal type classification for showing appropriate actions
  const COMPANY_SIGNAL_TYPES = ['sales_hiring', 'territory_expansion', 'branch_opening', 'acquisition', 'hiring_surge', 'market_growth', 'franchise_activity'];
  const PROPERTY_SIGNAL_TYPES = ['new_hotel', 'new_opening', 'renovation', 'rebrand', 'ownership_change', 'expansion', 'new_gm', 'construction', 'development_permits', 'new_brand'];
  
  function classifySignalType(signalType) {
    if (COMPANY_SIGNAL_TYPES.includes(signalType)) return 'company';
    if (PROPERTY_SIGNAL_TYPES.includes(signalType)) return 'property';
    return 'hybrid';
  }

  async function handleCreateProspectCompany(signalId, signalCompanyName) {
    setCompanySignalId(signalId);
    setCompanyName(signalCompanyName || '');
    setCompanyDialogOpen(true);
  }

  async function submitCreateProspectCompany() {
    if (!companyName.trim()) {
      toast.error('Please enter a company name');
      return;
    }
    setConvertingCompany(true);
    try {
      const data = await api.post(`/market-intelligence/signals/${companySignalId}/create-prospect-company`);
      if (data.success) {
        toast.success(data.is_new ? 'Prospect company created!' : 'Signal linked to existing prospect');
        setCompanyDialogOpen(false);
        setCompanySignalId(null);
        setCompanyName('');
        fetchSignals();
      } else {
        toast.error(data.detail || 'Failed to create prospect company');
      }
    } catch (err) {
      toast.error('Error creating prospect company');
    } finally {
      setConvertingCompany(false);
    }
  }

  async function handleGenerateOpportunity(signalId, signalCompanyName) {
    // Opens dialog to confirm and generate opportunity
    setCompanySignalId(signalId);
    setCompanyName(signalCompanyName || '');
    // Use a flag to distinguish between prospect creation and opportunity generation
    setConvertingCompany(true);
    try {
      const data = await api.post(`/market-intelligence/signals/${signalId}/generate-opportunity?company_name=${encodeURIComponent(signalCompanyName || '')}`);
      if (data.success) {
        toast.success(`Opportunity generated! Value: $${data.estimated_value?.toLocaleString()}`);
        fetchSignals();
        // Navigate to the opportunity
        navigate(`/opportunities/${data.opportunity_id}`);
      } else {
        toast.error(data.detail || 'Failed to generate opportunity');
      }
    } catch (err) {
      toast.error('Error generating opportunity');
    } finally {
      setConvertingCompany(false);
    }
  }

  // ==================== END NEW COMPANY CONVERSION HANDLERS ====================

  // ==================== SMART DISCOVERY HANDLERS (ADDITIVE) ====================
  // These handlers add AI-powered company discovery WITHOUT modifying existing flows
  
  async function handleSmartDiscovery(signal) {
    setDiscoverySignal(signal);
    setDiscoveredCompanies([]);
    setSelectedCompanyIndex(0);
    setEnrichmentData(null);
    setDiscoveryStep('discover');
    setDiscoveryDialogOpen(true);
    
    // Start discovery immediately
    setDiscoveryLoading(true);
    try {
      const data = await api.post(`/market-intelligence/signals/${signal.id}/discover-companies`);
      if (data.success && data.companies?.length > 0) {
        setDiscoveredCompanies(data.companies);
        setDiscoveryStep('select');
        toast.success(`Found ${data.companies.length} potential companies`);
      } else {
        toast.info('No confident matches found. You can enter a company manually.');
        setDiscoveryStep('manual');
      }
    } catch (err) {
      toast.error('Discovery failed. Try manual entry.');
      setDiscoveryStep('manual');
    } finally {
      setDiscoveryLoading(false);
    }
  }

  async function handleEnrichSelectedCompany() {
    if (!discoveredCompanies[selectedCompanyIndex]) return;
    
    setDiscoveryLoading(true);
    try {
      const data = await api.post(`/market-intelligence/signals/${discoverySignal.id}/enrich-company?company_index=${selectedCompanyIndex}`);
      if (data.success) {
        setEnrichmentData(data.enrichment);
        setDiscoveryStep('confirm');
        toast.success('Company enriched with buyer intelligence');
      } else {
        toast.error('Enrichment failed');
      }
    } catch (err) {
      toast.error('Error enriching company');
    } finally {
      setDiscoveryLoading(false);
    }
  }

  async function handleSmartGenerateOpportunity() {
    setDiscoveryLoading(true);
    try {
      const company = discoveredCompanies[selectedCompanyIndex];
      const endpoint = companyName 
        ? `/market-intelligence/signals/${discoverySignal.id}/smart-generate-opportunity?company_name=${encodeURIComponent(companyName)}`
        : `/market-intelligence/signals/${discoverySignal.id}/smart-generate-opportunity?company_index=${selectedCompanyIndex}&skip_discovery=true`;
      
      const data = await api.post(endpoint);
      
      if (data.success) {
        toast.success(`Smart opportunity created: ${data.opportunity_name} - $${data.deal_value?.toLocaleString()}`);
        setDiscoveryDialogOpen(false);
        resetDiscoveryState();
        fetchSignals();
        navigate(`/opportunities/${data.opportunity_id}`);
      } else if (data.requires_manual_input) {
        toast.info('Please enter a company name manually');
        setDiscoveryStep('manual');
      } else {
        toast.error(data.error || 'Failed to generate opportunity');
      }
    } catch (err) {
      toast.error('Error generating opportunity');
    } finally {
      setDiscoveryLoading(false);
    }
  }

  function resetDiscoveryState() {
    setDiscoverySignal(null);
    setDiscoveredCompanies([]);
    setSelectedCompanyIndex(0);
    setEnrichmentData(null);
    setDiscoveryStep('discover');
    setCompanyName('');
  }

  // ==================== END SMART DISCOVERY HANDLERS ====================

  async function handleReGeocode(signalId) {
    try {
      const data = await api.post(`/market-intelligence/signals/${signalId}/re-geocode`);
      if (data.success) {
        toast.success('Location geocoded successfully');
        fetchSignals();
      } else {
        toast.error(data.message || 'Geocoding failed - check API key');
      }
    } catch (err) {
      toast.error('Error geocoding location');
    }
  }

  function resetForm() {
    setFormData({
      company_name: '',
      city: '',
      province_state: '',
      country: '',
      signal_type: '',
      description: '',
      source_link: '',
      signal_strength: 'medium',
      signal_confidence: 'medium',
      market_segment: '',
      location_scope: 'city'
    });
    setSelectedPrompt('');
    setSignalCategory('property');
  }

  const propertySignals = signals.filter(s => s.signal_category === 'property' || !s.signal_category);
  const marketSignals = signals.filter(s => s.signal_category === 'market');
  const failedGeoSignals = signals.filter(s => s.geo && s.geo.geocode_status !== 'success' && s.location?.city);

  async function handleReGeocodeAll() {
    if (failedGeoSignals.length === 0) return;
    toast.info(`Re-geocoding ${failedGeoSignals.length} signals...`);
    let successCount = 0;
    for (const signal of failedGeoSignals) {
      try {
        const data = await api.post(`/market-intelligence/signals/${signal.id}/re-geocode`);
        if (data.success) successCount++;
      } catch (err) {
        console.error('Error geocoding:', signal.id);
      }
    }
    toast.success(`Geocoded ${successCount} of ${failedGeoSignals.length} signals`);
    fetchSignals();
  }

  return (
    <div className="space-y-6">
      {/* Phase 3 — Suggested Signals (Draft) section */}
      <SuggestedSignalsDraftSection onConfirmedSignal={() => fetchSignals()} />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-[#1A1A1A]">Opportunity Radar</h2>
          <p className="text-sm text-[#6B7280]">Track signals sorted by opportunity trigger score</p>
        </div>
        <div className="flex items-center gap-2">
          {failedGeoSignals.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleReGeocodeAll} className="text-blue-600 border-blue-200 hover:bg-blue-50">
              <MapPin className="w-4 h-4 mr-1" />Re-geocode All ({failedGeoSignals.length})
            </Button>
          )}
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Signals</SelectItem>
              <SelectItem value="property">Property</SelectItem>
              <SelectItem value="market">Market</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={function() { setDialogOpen(true); }} className="bg-[#1A1A1A] hover:bg-[#333]">
            <Plus className="w-4 h-4 mr-2" />Add Signal
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" /></div>
      ) : signals.length === 0 ? (
        <Card className="border-[#E5E7EB]">
          <CardContent className="py-12 text-center">
            <Radar className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
            <p className="text-[#6B7280]">No active signals</p>
            <p className="text-sm text-[#9CA3AF] mt-1">Add a signal to start tracking market opportunities</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {signals.map(function(signal) {
            const strengthColor = SIGNAL_STRENGTH_COLORS[signal.signal_strength] || SIGNAL_STRENGTH_COLORS.medium;
            const confidenceColor = CONFIDENCE_COLORS[signal.signal_confidence] || CONFIDENCE_COLORS.medium;
            const score = signal.opportunity_trigger_score || 0;
            const scoreColor = getScoreColor(score);
            const isMarket = signal.signal_category === 'market';
            const isHighlighted = signal.id === highlightedSignalId;
            const isExpanded = signal.id === expandedSignalId;
            // Improved display name logic - don't create fake "Market" names from cities
            const displayName = signal.company_name || signal.title || 
              (signal.location?.city && signal.signal_category === 'property' ? `${signal.location.city} Property` : 
               signal.signal_type ? `${signal.signal_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Signal` : 
               'Unnamed Signal');
            
            return (
              <Card 
                key={signal.id} 
                id={`signal-${signal.id}`}
                className={`border-[#E5E7EB] transition-all duration-300 cursor-pointer hover:shadow-md ${
                  isHighlighted ? 'ring-2 ring-[#B89B5E] shadow-lg bg-[#FFFEF5]' : ''
                }`}
                onClick={() => setExpandedSignalId(isExpanded ? null : signal.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant={isMarket ? "secondary" : "default"} className="text-xs">
                          {isMarket ? "MARKET" : "PROPERTY"}
                        </Badge>
                        {signal.industry && INDUSTRY_CONFIG[signal.industry] && (
                          <Badge className={INDUSTRY_CONFIG[signal.industry].bgColor + " text-xs"}>
                            {INDUSTRY_CONFIG[signal.industry].icon} {INDUSTRY_CONFIG[signal.industry].label}
                          </Badge>
                        )}
                        <h3 className="font-medium text-[#1A1A1A]">{displayName}</h3>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <div className={`px-3 py-1 rounded-full text-sm font-semibold ${scoreColor}`}>
                          Score: {score}
                        </div>
                        {signal.revenue_potential_score && REVENUE_POTENTIAL_CONFIG[signal.revenue_potential_score] && (
                          <Badge className={REVENUE_POTENTIAL_CONFIG[signal.revenue_potential_score].color + " text-xs border"}>
                            <DollarSign className="w-3 h-3 mr-1" />
                            {REVENUE_POTENTIAL_CONFIG[signal.revenue_potential_score].label}
                          </Badge>
                        )}
                        {signal.estimated_value_display && (
                          <span className="text-xs text-[#6B7280]">{signal.estimated_value_display}</span>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {SIGNAL_PROMPTS[signal.signal_type]?.split(' ').slice(0, 3).join(' ') || signal.signal_type}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge className={strengthColor + " text-xs"}>
                          {(signal.signal_strength || 'medium').toUpperCase()}
                        </Badge>
                        <Badge className={confidenceColor + " text-xs"}>
                          {(signal.signal_confidence || 'medium').toUpperCase()}
                        </Badge>
                        {/* Feb 2026 — Gold "Hiring Signal" badge for SerpAPI
                            LinkedIn/jobs signals. Active buying indicator
                            distinct from generic market news. */}
                        {(signal.is_hiring_signal ||
                          (signal.signal_tags || []).includes('hiring_signal')) && (
                          <Badge
                            className="text-xs bg-gradient-to-r from-[#C9A227] to-[#E0B73D] text-white border-0 shadow-sm"
                            data-testid={`hiring-signal-badge-${signal.id}`}
                            title="Active hiring signal — high-intent buying indicator"
                          >
                            <Target className="w-3 h-3 mr-1" />
                            Hiring Signal
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-[#6B7280] mb-2 line-clamp-2">{signal.description?.replace(/<[^>]*>/g, '').substring(0, 200)}</p>
                      <div className="flex items-center gap-4 text-xs text-[#9CA3AF]">
                        {signal.location?.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {[signal.location.city, signal.location.province_state, signal.location.country].filter(Boolean).join(', ')}
                          </span>
                        )}
                        {signal.source_link && (
                          <a 
                            href={signal.source_link.startsWith('http') ? signal.source_link : `https://${signal.source_link}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-[#C9A227] hover:underline cursor-pointer"
                          >
                            <ExternalLink className="w-3 h-3" />View Source
                          </a>
                        )}
                        {signal.source_name && (
                          <span className="text-[#6B7280]">via {signal.source_name}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 ml-4">
                      {/* EXISTING: Property conversion actions - PRESERVED */}
                      {isMarket ? (
                        <>
                          {/* Convert to Property / Add to Pipeline button - Now opens enhanced modal */}
                          {classifySignalType(signal.signal_type) !== 'company' && (
                            <Button variant="outline" size="sm" onClick={function() { 
                              setSignalForCreate(signal);
                              setCreateOppModalOpen(true);
                            }} data-testid={`convert-property-${signal.id}`}>
                              <TrendingUp className="w-4 h-4 mr-1" />Add to Pipeline
                            </Button>
                          )}
                          {/* NEW: Company conversion actions - ADDITIVE */}
                          {classifySignalType(signal.signal_type) !== 'property' && (
                            <div className="flex flex-col gap-1.5">
                              {/* Primary action - Add to Pipeline (now opens enhanced modal) */}
                              {!signal.linked_opportunity_id && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-[#C9A227] border-[#C9A227] hover:bg-[#C9A227]/10"
                                  onClick={function() { 
                                    setSignalForCreate(signal);
                                    setCreateOppModalOpen(true);
                                  }}
                                  disabled={convertingCompany}
                                  title="Create opportunity in pipeline"
                                  data-testid={`add-pipeline-company-${signal.id}`}
                                >
                                  <TrendingUp className="w-4 h-4 mr-1" />
                                  Add to Pipeline
                                </Button>
                              )}
                              {signal.linked_opportunity_id && (
                                <Badge variant="outline" className="text-green-600 border-green-200">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  In Pipeline
                                </Badge>
                              )}
                              {/* Secondary actions */}
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-purple-600 hover:bg-purple-50 text-xs h-7"
                                  onClick={function() { handleSmartDiscovery(signal); }}
                                  disabled={discoveryLoading || signal.linked_opportunity_id}
                                  title="AI-powered company discovery and enrichment"
                                >
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  Enrich
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-emerald-600 hover:bg-emerald-50 text-xs h-7"
                                  onClick={function() { handleCreateProspectCompany(signal.id, signal.company_name || signal.title); }}
                                  disabled={signal.linked_prospect_id}
                                  title={signal.linked_prospect_id ? 'Already converted to prospect' : 'Create Prospect only (no opportunity)'}
                                >
                                  <Users className="w-3 h-3 mr-1" />
                                  Prospect
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#8A6F2E] hover:bg-[#FAF4E6] text-xs h-7"
                                  onClick={function() {
                                    const company = signal.company_name || signal.title || '';
                                    const city = signal.location?.city || '';
                                    const params = new URLSearchParams({
                                      tab: 'prospects',
                                      prospects_view: 'search',
                                      discover_company: company,
                                      discover_auto: '1',
                                    });
                                    if (city) params.set('discover_city', city);
                                    navigate(`/market-intelligence?${params.toString()}`);
                                  }}
                                  title="Search decision-makers at this company"
                                  data-testid={`find-contacts-btn-${signal.id}`}
                                >
                                  <Search className="w-3 h-3 mr-1" />
                                  Find contacts →
                                </Button>
                                {/* Feb 2026 — Signal → Decision Maker.
                                    Smarter than "Find contacts": for
                                    hiring signals it targets the sales
                                    LEADER (VP Sales) — the person
                                    BUILDING the team — not the BDR being
                                    hired. For non-hiring signals it uses
                                    the seller's first Industry Architect
                                    buyer_role. */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#8A6F2E] hover:bg-[#FAF4E6] text-xs h-7"
                                  onClick={function() {
                                    const company = extractSignalCompany(signal);
                                    const title = determineDmJobTitle(signal, sellerProfile);
                                    const city = signal.location?.city || '';
                                    const params = new URLSearchParams({
                                      tab: 'prospects',
                                      prospects_view: 'search',
                                      discover_company: company,
                                      discover_title: title,
                                      discover_auto: '1',
                                    });
                                    if (city) params.set('discover_city', city);
                                    navigate(`/market-intelligence?${params.toString()}`);
                                  }}
                                  title={`Find ${determineDmJobTitle(signal, sellerProfile)} at ${extractSignalCompany(signal) || 'this company'}`}
                                  data-testid={`find-dm-btn-${signal.id}`}
                                >
                                  <Target className="w-3 h-3 mr-1" />
                                  Find Decision Maker
                                </Button>
                              </div>
                            </div>
                          )}
                          {/* For hybrid signals - show both pathways */}
                          {classifySignalType(signal.signal_type) === 'hybrid' && (
                            <p className="text-xs text-gray-400">Property or Company conversion available</p>
                          )}
                        </>
                      ) : (
                        <Button variant="outline" size="sm" onClick={function() { handleConvert(signal.id); }}>
                          <TrendingUp className="w-4 h-4 mr-1" />Create Opp
                        </Button>
                      )}
                      <div className="flex items-center gap-1">
                        {signal.geo && signal.geo.geocode_status !== 'success' && signal.location?.city && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={function() { handleReGeocode(signal.id); }}
                            title="Re-geocode location"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <MapPin className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={function() { handleDismiss(signal.id); }}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Signal Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Signal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Signal Category Toggle */}
            <div className="space-y-2">
              <Label>Signal Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={signalCategory === 'property' ? 'default' : 'outline'}
                  onClick={function() { setSignalCategory('property'); }}
                  className="flex-1"
                >
                  <Building2 className="w-4 h-4 mr-2" />Property Signal
                </Button>
                <Button
                  type="button"
                  variant={signalCategory === 'market' ? 'default' : 'outline'}
                  onClick={function() { setSignalCategory('market'); }}
                  className="flex-1"
                >
                  <Globe className="w-4 h-4 mr-2" />Market Signal
                </Button>
              </div>
              <p className="text-xs text-[#9CA3AF]">
                {signalCategory === 'property' 
                  ? 'Attach to a specific property or company' 
                  : 'Track regional market activity without a specific property'}
              </p>
            </div>

            {/* Quick Signal Type */}
            <div className="space-y-2">
              <Label>Signal Category</Label>
              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {Object.entries(SIGNAL_PROMPTS).map(function([key, label]) {
                  return (
                    <Button
                      key={key}
                      type="button"
                      variant={selectedPrompt === key ? "default" : "outline"}
                      size="sm"
                      onClick={function() { handlePromptSelect(key); }}
                      className="text-xs justify-start truncate"
                    >
                      {label.split(' ').slice(0, 2).join(' ')}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Company Name (only for property signals) */}
            {signalCategory === 'property' && (
              <div className="space-y-2">
                <Label>Company / Property Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={function(e) { setFormData(prev => ({ ...prev, company_name: e.target.value })); }}
                  placeholder="Acme Corp"
                  data-testid="add-signal-company-name-input"
                />
              </div>
            )}

            {/* Market Segment (only for market signals) */}
            {signalCategory === 'market' && (
              <div className="space-y-2">
                <Label>Market Segment</Label>
                <Select value={formData.market_segment} onValueChange={function(v) { setFormData(prev => ({ ...prev, market_segment: v })); }}>
                  <SelectTrigger data-testid="add-signal-market-segment-trigger">
                    <SelectValue placeholder={sellerTargetSegments.length ? "Select from your target segments" : "Select segment"} />
                  </SelectTrigger>
                  <SelectContent>
                    {sellerTargetSegments.length > 0
                      ? sellerTargetSegments.map(function(seg) {
                          const label = String(seg);
                          // Value uses the raw segment string so it round-trips
                          // through the API without a client-side enum mapping.
                          return (
                            <SelectItem
                              key={label}
                              value={label}
                              data-testid={`add-signal-market-segment-option-${label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {label}
                            </SelectItem>
                          );
                        })
                      : Object.entries(MARKET_SEGMENTS).map(function([key, label]) {
                          return (
                            <SelectItem
                              key={key}
                              value={key}
                              data-testid={`add-signal-market-segment-option-${key}`}
                            >
                              {label}
                            </SelectItem>
                          );
                        })}
                  </SelectContent>
                </Select>
                {sellerTargetSegments.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Configure your Target Market Segments in Intelligence Settings for a personalized list.
                  </p>
                )}
              </div>
            )}

            {/* Location Scope */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Location</Label>
                <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                  <Button
                    type="button"
                    size="sm"
                    variant={formData.location_scope === 'city' ? "default" : "ghost"}
                    onClick={function() { setFormData(prev => ({ ...prev, location_scope: 'city', city: '', province_state: '' })); }}
                    className="text-xs h-7 px-3"
                  >
                    City
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={formData.location_scope === 'country' ? "default" : "ghost"}
                    onClick={function() { setFormData(prev => ({ ...prev, location_scope: 'country', city: '', province_state: '' })); }}
                    className="text-xs h-7 px-3"
                  >
                    Country-wide
                  </Button>
                </div>
              </div>
              
              {formData.location_scope === 'city' ? (
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">City</Label>
                    <Input value={formData.city} onChange={function(e) { setFormData(prev => ({ ...prev, city: e.target.value })); }} placeholder="Toronto" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Province/State</Label>
                    <Input value={formData.province_state} onChange={function(e) { setFormData(prev => ({ ...prev, province_state: e.target.value })); }} placeholder="Ontario" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Country</Label>
                    <Input value={formData.country} onChange={function(e) { setFormData(prev => ({ ...prev, country: e.target.value })); }} placeholder="Canada" />
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Country</Label>
                  <Input value={formData.country} onChange={function(e) { setFormData(prev => ({ ...prev, country: e.target.value })); }} placeholder="Canada" />
                  <p className="text-xs text-gray-400">Signal applies to entire country</p>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={function(e) { setFormData(prev => ({ ...prev, description: e.target.value })); }} placeholder="Details about the signal..." rows={2} />
            </div>

            {/* Source, Strength, Confidence */}
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>Source Link</Label>
                <Input value={formData.source_link} onChange={function(e) { setFormData(prev => ({ ...prev, source_link: e.target.value })); }} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label>Strength</Label>
                <Select value={formData.signal_strength} onValueChange={function(v) { setFormData(prev => ({ ...prev, signal_strength: v })); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Confidence</Label>
                <Select value={formData.signal_confidence} onValueChange={function(v) { setFormData(prev => ({ ...prev, signal_confidence: v })); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low (Rumor)</SelectItem>
                    <SelectItem value="medium">Medium (Article)</SelectItem>
                    <SelectItem value="high">High (Official)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={function() { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button onClick={handleCreateSignal} className="bg-[#C9A227] hover:bg-[#B8922A]">Create Signal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert to Property Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add to Pipeline</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">
              Enter the property/company name to create a new opportunity in your pipeline.
            </p>
            <div className="space-y-2">
              <Label>Company / Property Name *</Label>
              <Input 
                value={convertCompanyName} 
                onChange={function(e) { setConvertCompanyName(e.target.value); }} 
                placeholder="Enter the specific property name"
                data-testid="convert-property-name-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={function() { setConvertDialogOpen(false); setConvertCompanyName(''); }}>Cancel</Button>
              <Button onClick={handleConvertToProperty} className="bg-[#C9A227] hover:bg-[#B8922A]" data-testid="convert-property-submit">
                <TrendingUp className="w-4 h-4 mr-1" />Add to Pipeline
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW: Create Prospect Company Dialog - ADDITIVE */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Create Prospect Company
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">
              This will create a prospect company record from this market signal, allowing you to track and convert this lead through the sales pipeline.
            </p>
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input 
                value={companyName} 
                onChange={function(e) { setCompanyName(e.target.value); }} 
                placeholder="Enter company name"
              />
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-gray-700 mb-1">What happens next:</p>
              <ul className="text-gray-600 space-y-1 list-disc list-inside">
                <li>A new prospect company will be created</li>
                <li>Signal data will be linked to this prospect</li>
                <li>You can then generate an opportunity from the prospect</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={function() { setCompanyDialogOpen(false); setCompanyName(''); }}>Cancel</Button>
              <Button 
                onClick={submitCreateProspectCompany} 
                disabled={convertingCompany || !companyName.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {convertingCompany ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                ) : (
                  'Create Prospect'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW: Smart Discovery Dialog - AI-powered company discovery */}
      <Dialog open={discoveryDialogOpen} onOpenChange={function(open) { if (!open) resetDiscoveryState(); setDiscoveryDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Smart Company Discovery
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Signal context */}
            {discoverySignal && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-gray-700">{discoverySignal.title || 'Market Signal'}</p>
                <p className="text-xs text-gray-500 mt-1">{discoverySignal.description?.substring(0, 150)}...</p>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline">{discoverySignal.signal_type?.replace('_', ' ')}</Badge>
                  <Badge variant="outline">{discoverySignal.market_segment}</Badge>
                </div>
              </div>
            )}

            {/* Step: Discovery Loading */}
            {discoveryStep === 'discover' && discoveryLoading && (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
                <p className="text-sm text-gray-600 mt-2">Discovering target companies...</p>
                <p className="text-xs text-gray-400">Analyzing signal context and market data</p>
              </div>
            )}

            {/* Step: Company Selection */}
            {discoveryStep === 'select' && discoveredCompanies.length > 0 && (
              <div className="space-y-3">
                <Label className="text-base">Select Target Company</Label>
                <p className="text-sm text-gray-500">Found {discoveredCompanies.length} potential companies. Select the best match:</p>
                
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {discoveredCompanies.map(function(company, index) {
                    return (
                      <div 
                        key={index}
                        onClick={function() { setSelectedCompanyIndex(index); }}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedCompanyIndex === index 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900">{company.name}</p>
                            <p className="text-xs text-gray-500">{company.description?.substring(0, 80)}</p>
                            <p className="text-xs text-gray-400 mt-1">{company.match_reason}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant={company.confidence >= 0.8 ? 'default' : 'outline'} className={company.confidence >= 0.8 ? 'bg-green-100 text-green-800' : ''}>
                              {Math.round(company.confidence * 100)}% match
                            </Badge>
                            {company.tier && (
                              <p className="text-xs text-gray-400 mt-1">{company.tier}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-between pt-2">
                  <Button variant="ghost" size="sm" onClick={function() { setDiscoveryStep('manual'); }}>
                    Enter manually instead
                  </Button>
                  <Button 
                    onClick={handleEnrichSelectedCompany}
                    disabled={discoveryLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {discoveryLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                    Enrich & Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Step: Manual Entry */}
            {discoveryStep === 'manual' && (
              <div className="space-y-3">
                <Label>Enter Company Name</Label>
                <p className="text-sm text-gray-500">No confident match found. Enter the target company manually:</p>
                <Input 
                  value={companyName}
                  onChange={function(e) { setCompanyName(e.target.value); }}
                  placeholder="Company name"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={function() { setDiscoveryStep('select'); }}>
                    Back to matches
                  </Button>
                  <Button 
                    onClick={handleSmartGenerateOpportunity}
                    disabled={!companyName.trim() || discoveryLoading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {discoveryLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Generate Opportunity
                  </Button>
                </div>
              </div>
            )}

            {/* Step: Confirmation with Enrichment */}
            {discoveryStep === 'confirm' && enrichmentData && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="font-medium text-gray-900">
                      {discoveredCompanies[selectedCompanyIndex]?.name}
                    </p>
                  </div>
                  
                  {/* AI Insights */}
                  {enrichmentData.ai_insights && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm text-gray-700">{enrichmentData.ai_insights.why_company_is_fit}</p>
                      
                      {enrichmentData.ai_insights.key_buyer_priorities && (
                        <div>
                          <p className="text-xs font-medium text-gray-600 mt-2">Key Buyer Priorities:</p>
                          <ul className="text-xs text-gray-500 list-disc list-inside">
                            {enrichmentData.ai_insights.key_buyer_priorities.slice(0, 3).map(function(p, i) {
                              return <li key={i}>{p}</li>;
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Buyer Roles */}
                  {enrichmentData.buyer_roles && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-600">Key Buyer Roles:</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {enrichmentData.buyer_roles.slice(0, 4).map(function(role, i) {
                          return (
                            <Badge key={i} variant="outline" className="text-xs">
                              {role.role}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Outreach Angles */}
                  {enrichmentData.outreach_angles && enrichmentData.outreach_angles[0] && (
                    <div className="mt-3 p-2 bg-white/50 rounded">
                      <p className="text-xs font-medium text-gray-600">Recommended Approach:</p>
                      <p className="text-xs text-gray-700">{enrichmentData.outreach_angles[0].angle}: {enrichmentData.outreach_angles[0].message}</p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={function() { setDiscoveryStep('select'); }}>
                    Select Different Company
                  </Button>
                  <Button 
                    onClick={handleSmartGenerateOpportunity}
                    disabled={discoveryLoading}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  >
                    {discoveryLoading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" />Create Smart Opportunity</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Enhanced Create Opportunity Modal */}
      <CreateOpportunityModal
        isOpen={createOppModalOpen}
        onClose={() => {
          setCreateOppModalOpen(false);
          setSignalForCreate(null);
        }}
        signal={signalForCreate}
        onSuccess={() => {
          fetchSignals();
        }}
      />
    </div>
  );
}

// ===================== PROSPECTS TAB =====================

function ProspectsTab({ token, viewingAsUser }) {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  // Sub-tab switcher: 'search' (Discover Prospects) | 'lists' (My Lists) | 'manual'
  const [subTab, setSubTab] = useState(searchParams.get('prospects_view') || 'search');
  const discoverInitialCompany = searchParams.get('discover_company') || '';
  const discoverInitialTitle = searchParams.get('discover_title') || '';
  const discoverAutoRun = searchParams.get('discover_auto') === '1';
  const [formData, setFormData] = useState({
    company_name: '',
    city: '',
    province_state: '',
    country: '',
    industry: 'hotels',
    estimated_team_size: '',
    website: '',
    linkedin_url: '',
    target_department: '',
    notes: ''
  });

  useEffect(function() { fetchProspects(); }, [viewingAsUser?.id]);

  async function fetchProspects() {
    setLoading(true);
    try {
      const data = await api.get('/market-intelligence/prospects');
      setProspects(data || []);
    } catch (err) {
      console.error('Error fetching prospects:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateProspect() {
    if (!formData.company_name) {
      toast.error('Please enter a company name');
      return;
    }

    try {
      await api.post('/market-intelligence/prospects', {
        company_name: formData.company_name,
        location: {
          city: formData.city,
          province_state: formData.province_state,
          country: formData.country
        },
        industry: formData.industry,
        estimated_team_size: formData.estimated_team_size,
        website: formData.website,
        linkedin_url: formData.linkedin_url,
        target_department: formData.target_department,
        notes: formData.notes
      });

      toast.success('Prospect added - Account, Contact & Opportunity created');
      setDialogOpen(false);
      resetForm();
      fetchProspects();
    } catch (err) {
      toast.error('Error creating prospect');
    }
  }

  function resetForm() {
    setFormData({
      company_name: '',
      city: '',
      province_state: '',
      country: '',
      industry: 'Hospitality',
      estimated_team_size: '',
      website: '',
      linkedin_url: '',
      target_department: '',
      notes: ''
    });
  }

  function switchSubTab(value) {
    setSubTab(value);
    // Keep URL in sync, preserve tab=prospects + any signal param
    const newParams = {};
    for (const [k, v] of searchParams.entries()) {
      if (k === 'prospects_view' || k === 'discover_company' ||
          k === 'discover_auto' || k === 'discover_city' ||
          k === 'discover_title') continue;
      newParams[k] = v;
    }
    newParams.tab = 'prospects';
    newParams.prospects_view = value;
    setSearchParams(newParams);
  }

  // Pre-fill the "Add Prospect" form when a contact from search/lists is sent over
  function handleAddSearchedToManual(c) {
    setFormData({
      company_name: c.organization_name || '',
      city: c.city || '',
      province_state: '',
      country: 'Canada',
      industry: 'hotels',
      estimated_team_size: '',
      website: '',
      linkedin_url: c.linkedin_url || '',
      target_department: '',
      notes: [
        c.first_name && c.last_name ? `${c.first_name} ${c.last_name}` : null,
        c.title || null,
        c.email ? `Email: ${c.email}` : null,
        c.phone ? `Phone: ${c.phone}` : null,
      ].filter(Boolean).join(' · '),
    });
    setSubTab('manual');
    setDialogOpen(true);
    toast.success(`Pre-filled prospect from ${c.organization_name || 'search'}`);
  }

  // Add a discovered prospect-list contact as a Contact in the CRM
  async function handleAddToContacts(c) {
    try {
      await api.post('/contacts', {
        first_name: c.first_name || '',
        last_name: c.last_name || '',
        email: c.email || '',
        phone: c.phone || '',
        title: c.title || '',
        company_name: c.organization_name || '',
        linkedin_url: c.linkedin_url || '',
        source: 'prospect_search',
      });
    } catch (err) {
      // best-effort — toast handled by caller batch
    }
  }

  function handleGenerateOutreach(c) {
    if (!c.email) {
      toast.error('Reveal email first');
      return;
    }
    navigate(`/build-and-send?prefill_email=${encodeURIComponent(c.email)}&prefill_name=${encodeURIComponent([c.first_name, c.last_name].filter(Boolean).join(' '))}&prefill_company=${encodeURIComponent(c.organization_name || '')}`);
  }

  return (
    <div className="space-y-6" data-testid="prospects-tab">
      {/* Sub-tab switcher */}
      <div className="flex gap-1 bg-muted/50 rounded-sm p-1 w-fit">
        <button
          type="button"
          onClick={() => switchSubTab('search')}
          className={`px-4 py-1.5 rounded-sm text-sm font-medium transition-colors ${
            subTab === 'search'
              ? 'bg-card text-[#1A1A1A] shadow-sm'
              : 'text-muted-foreground hover:text-[#1A1A1A]'
          }`}
          data-testid="prospects-subtab-search"
        >
          <Search className="w-3.5 h-3.5 inline mr-1.5" />
          Search
        </button>
        <button
          type="button"
          onClick={() => switchSubTab('lists')}
          className={`px-4 py-1.5 rounded-sm text-sm font-medium transition-colors ${
            subTab === 'lists'
              ? 'bg-card text-[#1A1A1A] shadow-sm'
              : 'text-muted-foreground hover:text-[#1A1A1A]'
          }`}
          data-testid="prospects-subtab-lists"
        >
          <Target className="w-3.5 h-3.5 inline mr-1.5" />
          My Lists
        </button>
        <button
          type="button"
          onClick={() => switchSubTab('manual')}
          className={`px-4 py-1.5 rounded-sm text-sm font-medium transition-colors ${
            subTab === 'manual'
              ? 'bg-card text-[#1A1A1A] shadow-sm'
              : 'text-muted-foreground hover:text-[#1A1A1A]'
          }`}
          data-testid="prospects-subtab-manual"
        >
          <UserPlus className="w-3.5 h-3.5 inline mr-1.5" />
          Manual
        </button>
      </div>

      {subTab === 'search' && (
        <DiscoverProspectsPanel
          api={api}
          initialCompany={discoverInitialCompany}
          initialTitle={discoverInitialTitle}
          autoRun={discoverAutoRun}
          onAddToManualProspects={handleAddSearchedToManual}
          onAddToContacts={handleAddToContacts}
          onGenerateOutreach={handleGenerateOutreach}
        />
      )}

      {subTab === 'lists' && (
        <MyListsView api={api} onAddToContacts={handleAddToContacts} />
      )}

      {subTab === 'manual' && (
      <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-[#1A1A1A]">Manual Discovery</h2>
          <p className="text-sm text-[#6B7280]">Capture prospects from LinkedIn, Google, referrals</p>
        </div>
        <Button onClick={function() { setDialogOpen(true); }} className="bg-[#1A1A1A] hover:bg-[#333]">
          <Plus className="w-4 h-4 mr-2" />Add Prospect
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" /></div>
      ) : prospects.length === 0 ? (
        <Card className="border-[#E5E7EB]">
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
            <p className="text-[#6B7280]">No prospects yet</p>
            <p className="text-sm text-[#9CA3AF] mt-1">Add a prospect to create an Account, Contact & Opportunity</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {prospects.map(function(prospect) {
            return (
              <Card 
                key={prospect.id} 
                className="border-[#E5E7EB] cursor-pointer hover:border-[#C9A227] hover:shadow-md transition-all"
                onClick={function() { setSelectedProspect(prospect); setDetailDialogOpen(true); }}
                data-testid={`prospect-card-${prospect.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-[#1A1A1A]">{prospect.company_name}</h3>
                    <Badge variant="outline" className="text-xs">{prospect.status}</Badge>
                  </div>
                  <div className="space-y-1 text-sm text-[#6B7280]">
                    {prospect.location?.city && (
                      <p className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[prospect.location.city, prospect.location.province_state, prospect.location.country].filter(Boolean).join(', ')}
                      </p>
                    )}
                    <p className="flex items-center gap-1">
                      <Building2 className="w-3 h-3" />{prospect.industry}
                      {prospect.estimated_team_size && ` • ${prospect.estimated_team_size} employees`}
                    </p>
                    {prospect.target_department && (
                      <p className="flex items-center gap-1">
                        <Users className="w-3 h-3" />Target: {DEPARTMENTS.find(d => d.value === prospect.target_department)?.label || prospect.target_department}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    {prospect.website && (
                      <a href={prospect.website.startsWith('http') ? prospect.website : `https://${prospect.website}`} target="_blank" rel="noopener noreferrer" onClick={function(e) { e.stopPropagation(); }}>
                        <Button variant="ghost" size="sm"><Globe className="w-3 h-3" /></Button>
                      </a>
                    )}
                    {prospect.linkedin_url && (
                      <a href={prospect.linkedin_url.startsWith('http') ? prospect.linkedin_url : `https://${prospect.linkedin_url}`} target="_blank" rel="noopener noreferrer" onClick={function(e) { e.stopPropagation(); }}>
                        <Button variant="ghost" size="sm"><ExternalLink className="w-3 h-3" /></Button>
                      </a>
                    )}
                    <div className="ml-auto text-xs text-[#9CA3AF]">Click for details</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Prospect</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Company / Property Name *</Label>
              <Input value={formData.company_name} onChange={function(e) { setFormData(prev => ({ ...prev, company_name: e.target.value })); }} placeholder="The Ritz-Carlton" />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={formData.city} onChange={function(e) { setFormData(prev => ({ ...prev, city: e.target.value })); }} placeholder="Chicago" />
              </div>
              <div className="space-y-2">
                <Label>Province/State</Label>
                <Input value={formData.province_state} onChange={function(e) { setFormData(prev => ({ ...prev, province_state: e.target.value })); }} placeholder="Illinois" />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input value={formData.country} onChange={function(e) { setFormData(prev => ({ ...prev, country: e.target.value })); }} placeholder="USA" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Industry</Label>
                <Select value={formData.industry} onValueChange={function(v) { setFormData(prev => ({ ...prev, industry: v })); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(INDUSTRY_CONFIG).map(function([key, config]) {
                      return <SelectItem key={key} value={key}>{config.icon} {config.label}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estimated Team Size</Label>
                <Input value={formData.estimated_team_size} onChange={function(e) { setFormData(prev => ({ ...prev, estimated_team_size: e.target.value })); }} placeholder="100-500" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Website</Label>
                <Input value={formData.website} onChange={function(e) { setFormData(prev => ({ ...prev, website: e.target.value })); }} placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label>LinkedIn Page</Label>
                <Input value={formData.linkedin_url} onChange={function(e) { setFormData(prev => ({ ...prev, linkedin_url: e.target.value })); }} placeholder="https://linkedin.com/company/..." />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Target Department</Label>
              <Select value={formData.target_department} onValueChange={function(v) { setFormData(prev => ({ ...prev, target_department: v })); }}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map(function(dept) {
                    return <SelectItem key={dept.value} value={dept.value}>{dept.label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={function(e) { setFormData(prev => ({ ...prev, notes: e.target.value })); }} placeholder="Additional notes..." rows={2} />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={function() { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button onClick={handleCreateProspect} className="bg-[#C9A227] hover:bg-[#B8922A]">Add Prospect</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prospect Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedProspect?.company_name}
            </DialogTitle>
          </DialogHeader>
          {selectedProspect && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-sm">{selectedProspect.status}</Badge>
                {selectedProspect.industry && (
                  <Badge className="bg-[#C9A227]/10 text-[#C9A227] border-[#C9A227]/20">
                    {selectedProspect.industry.charAt(0).toUpperCase() + selectedProspect.industry.slice(1)}
                  </Badge>
                )}
              </div>

              {/* Location */}
              {selectedProspect.location && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#6B7280]" />
                  <div>
                    <p className="font-medium text-[#1A1A1A]">Location</p>
                    <p className="text-[#6B7280]">
                      {[selectedProspect.location.city, selectedProspect.location.province_state, selectedProspect.location.country].filter(Boolean).join(', ')}
                    </p>
                  </div>
                </div>
              )}

              {/* Team Size */}
              {selectedProspect.estimated_team_size && (
                <div className="flex items-start gap-2 text-sm">
                  <Users className="w-4 h-4 mt-0.5 text-[#6B7280]" />
                  <div>
                    <p className="font-medium text-[#1A1A1A]">Estimated Team Size</p>
                    <p className="text-[#6B7280]">{selectedProspect.estimated_team_size} employees</p>
                  </div>
                </div>
              )}

              {/* Target Department */}
              {selectedProspect.target_department && (
                <div className="flex items-start gap-2 text-sm">
                  <Target className="w-4 h-4 mt-0.5 text-[#6B7280]" />
                  <div>
                    <p className="font-medium text-[#1A1A1A]">Target Department</p>
                    <p className="text-[#6B7280]">
                      {DEPARTMENTS.find(d => d.value === selectedProspect.target_department)?.label || selectedProspect.target_department}
                    </p>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedProspect.notes && (
                <div className="flex items-start gap-2 text-sm">
                  <Eye className="w-4 h-4 mt-0.5 text-[#6B7280]" />
                  <div>
                    <p className="font-medium text-[#1A1A1A]">Notes</p>
                    <p className="text-[#6B7280] whitespace-pre-wrap">{selectedProspect.notes}</p>
                  </div>
                </div>
              )}

              {/* Source Signal Info */}
              {selectedProspect.source_signal && (
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <p className="font-medium text-blue-700 flex items-center gap-1">
                    <Zap className="w-4 h-4" /> Discovered via Market Signal
                  </p>
                  {selectedProspect.revenue_potential && (
                    <p className="text-blue-600 mt-1">
                      Revenue Potential: {selectedProspect.revenue_potential.label} (${selectedProspect.revenue_potential.min?.toLocaleString()} - ${selectedProspect.revenue_potential.max?.toLocaleString()})
                    </p>
                  )}
                </div>
              )}

              {/* Links */}
              <div className="flex items-center gap-2 pt-2 border-t">
                {selectedProspect.website && (
                  <a href={selectedProspect.website.startsWith('http') ? selectedProspect.website : `https://${selectedProspect.website}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="gap-1">
                      <Globe className="w-3 h-3" /> Website
                    </Button>
                  </a>
                )}
                {selectedProspect.linkedin_url && (
                  <a href={selectedProspect.linkedin_url.startsWith('http') ? selectedProspect.linkedin_url : `https://${selectedProspect.linkedin_url}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="gap-1">
                      <ExternalLink className="w-3 h-3" /> LinkedIn
                    </Button>
                  </a>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={function() { setDetailDialogOpen(false); }}>Close</Button>
                {selectedProspect.account_id ? (
                  <Link to={`/account/${selectedProspect.account_id}`}>
                    <Button className="bg-[#1A1A1A] hover:bg-[#333] gap-1">
                      <Eye className="w-4 h-4" /> View Account
                    </Button>
                  </Link>
                ) : (
                  <Button className="bg-[#C9A227] hover:bg-[#B8922A] gap-1" disabled>
                    <Briefcase className="w-4 h-4" /> Pending Conversion
                  </Button>
                )}
              </div>

              {/* Created Date */}
              <p className="text-xs text-[#9CA3AF] text-center">
                Added: {new Date(selectedProspect.created_at).toLocaleDateString()}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}

// ===================== MAP TAB =====================

// ===================== LUXURY MAP CONSTANTS =====================

// Luxury DH Design brand marker colors
const LUXURY_MARKER_COLORS = {
  hotel: '#B89B5E',       // Luxury gold for hotels
  corporate: '#1C1C1C',   // Charcoal for corporate
  renovation: '#D4A76A',  // Amber for renovations
  restaurant: '#6F6F6F',  // Slate for restaurants
  prospect: '#8B8B8B',    // Muted gray for prospects
  opportunity: '#B89B5E', // Gold for opportunities
  closed: '#2D5016',      // Deep green for won
  default: '#6F6F6F'
};

// Custom map styling for luxury feel
const LUXURY_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ saturation: -20 }, { lightness: 5 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#1C1C1C" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#F7F5F2" }, { weight: 2 }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#B89B5E" }, { weight: 0.5 }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#E8E4DF" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#C5D4E0" }, { saturation: -30 }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#F0ECE6" }] }
];

// Estimate opportunity value based on property type and staff
function estimateOpportunityValue(data) {
  const staffCount = data?.estimated_staff || data?.employee_count || 50;
  const propertyType = data?.property_type || data?.industry || 'hotel';
  
  let basePerStaff = 500; // $500 per staff member
  if (propertyType === 'hotels' || propertyType === 'hotel') basePerStaff = 550;
  if (propertyType === 'corporate') basePerStaff = 400;
  if (propertyType === 'restaurant' || propertyType === 'restaurants') basePerStaff = 350;
  
  return Math.round(staffCount * basePerStaff);
}

// Get marker color based on signal/opportunity type
function getLuxuryMarkerColor(type, data) {
  if (type === 'signal') {
    const signalType = data?.signal_type || '';
    if (signalType.includes('renovation') || signalType.includes('rebrand')) {
      return LUXURY_MARKER_COLORS.renovation;
    }
    const industry = data?.industry || '';
    if (industry === 'hotels' || industry.includes('hotel')) return LUXURY_MARKER_COLORS.hotel;
    if (industry === 'restaurants' || industry.includes('restaurant')) return LUXURY_MARKER_COLORS.restaurant;
    if (industry === 'corporate') return LUXURY_MARKER_COLORS.corporate;
    return LUXURY_MARKER_COLORS.hotel;
  }
  if (type === 'opportunity') return LUXURY_MARKER_COLORS.opportunity;
  if (type === 'closed') return LUXURY_MARKER_COLORS.closed;
  if (type === 'prospect') return LUXURY_MARKER_COLORS.prospect;
  return LUXURY_MARKER_COLORS.default;
}

// Luxury circular marker component with radar pulse for new signals
function LuxuryMarker({ position, type, data, onClick, isSelected, isHovered, isNew }) {
  const color = getLuxuryMarkerColor(type, data);
  const size = isSelected ? 44 : isHovered ? 40 : 32;
  
  return (
    <AdvancedMarker position={position} onClick={onClick}>
      <div 
        className="relative cursor-pointer transition-all duration-300 ease-out"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        style={{ 
          width: size, 
          height: size,
          filter: isSelected || isHovered ? `drop-shadow(0 0 12px ${color}60)` : 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
        }}
        data-testid={`marker-${type}-${data?.id || 'unknown'}`}
      >
        {/* Radar pulse animation for new signals */}
        {isNew && (
          <>
            {/* First radar ring */}
            <div 
              className="absolute rounded-full"
              style={{ 
                inset: '-50%',
                backgroundColor: 'transparent',
                border: `2px solid ${color}`,
                animation: 'radar-ring 2s ease-out infinite',
                opacity: 0.6
              }}
            />
            {/* Second radar ring (delayed) */}
            <div 
              className="absolute rounded-full"
              style={{ 
                inset: '-50%',
                backgroundColor: 'transparent',
                border: `2px solid ${color}`,
                animation: 'radar-ring 2s ease-out infinite 0.5s',
                opacity: 0.4
              }}
            />
            {/* Center glow */}
            <div 
              className="absolute inset-0 rounded-full animate-pulse"
              style={{ 
                backgroundColor: color,
                opacity: 0.3,
                transform: 'scale(1.2)'
              }}
            />
          </>
        )}
        
        {/* Main circular marker */}
        <div 
          className="w-full h-full rounded-full border-2 border-white flex items-center justify-center transition-all duration-200 relative z-10"
          style={{ 
            backgroundColor: color,
            boxShadow: isSelected || isHovered 
              ? `0 0 20px ${color}50, 0 4px 12px rgba(0,0,0,0.15)` 
              : '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          {/* Inner icon */}
          {type === 'opportunity' || type === 'signal' ? (
            <DollarSign className="text-white" style={{ width: size * 0.4, height: size * 0.4 }} />
          ) : type === 'closed' ? (
            <CheckCircle className="text-white" style={{ width: size * 0.4, height: size * 0.4 }} />
          ) : (
            <Building2 className="text-white" style={{ width: size * 0.4, height: size * 0.4 }} />
          )}
        </div>
        
        {/* Selection ring */}
        {isSelected && (
          <div 
            className="absolute inset-0 rounded-full border-2 animate-pulse"
            style={{ 
              borderColor: color,
              transform: 'scale(1.4)',
              opacity: 0.5
            }}
          />
        )}
      </div>
    </AdvancedMarker>
  );
}

// Cluster marker component
function ClusterMarker({ position, count, totalValue, onClick }) {
  const displayValue = totalValue >= 1000000 
    ? `$${(totalValue / 1000000).toFixed(1)}M` 
    : `$${Math.round(totalValue / 1000)}K`;
  
  return (
    <AdvancedMarker position={position} onClick={onClick}>
      <div 
        className="cursor-pointer transition-all duration-300 hover:scale-110"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        style={{ 
          filter: 'drop-shadow(0 4px 12px rgba(184, 155, 94, 0.3))'
        }}
        data-testid="cluster-marker"
      >
        <div 
          className="rounded-full border-3 border-white flex flex-col items-center justify-center text-white font-medium"
          style={{ 
            width: 64,
            height: 64,
            backgroundColor: '#B89B5E',
            background: 'linear-gradient(145deg, #C5A869 0%, #B89B5E 50%, #A68B4E 100%)'
          }}
        >
          <span className="text-sm font-semibold">{count}</span>
          <span className="text-[10px] opacity-90">{displayValue}</span>
        </div>
      </div>
    </AdvancedMarker>
  );
}

// Luxury Intelligence Card popup
// Opportunity Strength indicator based on probability
function getOpportunityStrength(probability) {
  if (probability >= 60) return { label: 'Strong Opportunity', color: '#10B981', bgColor: '#10B98115', icon: '🟢' };
  if (probability >= 30) return { label: 'Moderate Opportunity', color: '#F59E0B', bgColor: '#F59E0B15', icon: '🟡' };
  return { label: 'Early Signal', color: '#EF4444', bgColor: '#EF444415', icon: '🔴' };
}

// Enhanced Intelligence Panel with actionable buttons
function IntelligenceCard({ marker, onClose, onViewOpportunity, onCreateDeal, onAddNote, token }) {
  const navigate = useNavigate();
  const api = useApi();
  const data = marker.data;
  const estimatedValue = data?.value || data?.deal_value || estimateOpportunityValue(data);
  const estimatedStaff = data?.estimated_staff || data?.employee_count || 50;
  const probability = data?.probability || (marker.type === 'opportunity' ? 40 : marker.type === 'signal' ? 20 : 10);
  const strength = getOpportunityStrength(probability);
  
  const [actionLoading, setActionLoading] = useState(null);
  
  const signalTypeLabel = data?.signal_type 
    ? SIGNAL_PROMPTS[data.signal_type] || data.signal_type.replace(/_/g, ' ')
    : marker.type === 'opportunity' ? 'Active Opportunity' : 'Market Signal';
  
  const propertyType = data?.property_type || data?.industry || 'Hotel';
  const propertyName = data?.company_name || data?.property_name || 'Unknown Property';
  const location = `${data?.city || 'Unknown City'}${data?.province_state ? `, ${data.province_state}` : ''}`;
  
  // Create Prospect action
  const handleCreateProspect = async () => {
    setActionLoading('prospect');
    try {
      const prospect = await api.post('/market-intelligence/prospects', {
        property_name: propertyName,
        property_type: propertyType,
        city: data?.city || '',
        province_state: data?.province_state || '',
        country: data?.country || 'Canada',
        estimated_staff: estimatedStaff,
        estimated_value: estimatedValue,
        source: 'opportunity_map',
        signal_id: data?.id,
        signal_type: data?.signal_type || 'market_signal',
        notes: `Created from Opportunity Map. ${signalTypeLabel}. Probability: ${probability}%`
      });
      
      toast.success('Prospect created successfully!', {
        action: {
          label: 'View',
          onClick: () => navigate('/market-intelligence?tab=prospects')
        }
      });
      onClose();
    } catch (err) {
      toast.error(err.message || 'Error creating prospect');
    } finally {
      setActionLoading(null);
    }
  };
  
  // Find Decision Maker action
  const handleFindDecisionMaker = async () => {
    setActionLoading('decision-maker');
    try {
      const result = await api.post('/intelligence/discover-decision-makers', {
        company_name: propertyName,
        location: data?.city || '',
        industry: 'hospitality'
      });
      
      if (result.total_discovered > 0) {
        toast.success(`Found ${result.total_discovered} decision makers!`);
        onClose();
        // Navigate to decision maker discovery page with search params to show results
        navigate(`/decision-maker-discovery?company=${encodeURIComponent(propertyName)}&location=${encodeURIComponent(data?.city || '')}`);
      } else {
        toast.info('No decision makers found. Try adjusting the search.');
        onClose();
      }
    } catch (err) {
      toast.error('Error finding decision makers');
    } finally {
      setActionLoading(null);
    }
  };
  
  // Generate Vision Deck action
  const handleGenerateVisionDeck = async () => {
    setActionLoading('vision-deck');
    try {
      // First create the deck
      const deck = await api.post('/vision-decks', {
        name: `${propertyName} Vision Deck`,
        template_id: 'modern-executive',
        opportunity_name: propertyName,
        pages: [{
          id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
          name: 'Cover',
          blocks: [
            {
              id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
              type: 'text',
              content: { 
                content: propertyName, 
                size: 'text-4xl', 
                  bold: true, 
                  align: 'center',
                  font: 'font-serif'
                }
              },
              {
                id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
                type: 'divider',
                content: { width: '60%', thickness: 1, style: 'solid', color: '#B89B5E' }
              },
              {
                id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
                type: 'text',
                content: { 
                  content: `Premium Uniform Collection\n${location}`, 
                  size: 'text-xl', 
                  align: 'center'
                }
              }
            ]
          }]
        });
      
      toast.success('Vision Deck created!', {
        action: {
          label: 'Edit',
          onClick: () => navigate(`/vision-decks/${deck.id}/edit`)
        }
      });
      onClose();
    } catch (err) {
      toast.error('Error creating Vision Deck');
    } finally {
      setActionLoading(null);
    }
  };
  
  return (
    <div 
      className="rounded-sm overflow-hidden"
      style={{ 
        backgroundColor: '#F7F5F2',
        border: '1px solid #E8E4DF',
        minWidth: 320,
        maxWidth: 380,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
      }}
    >
      {/* Header with Strength Indicator */}
      <div className="px-5 py-4 border-b border-[#E8E4DF]">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 
              className="font-serif text-lg leading-tight mb-1"
              style={{ color: '#1C1C1C', fontFamily: "'Playfair Display', serif" }}
            >
              {propertyName}
            </h3>
            <p className="text-xs text-[#6F6F6F] flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {location}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-[#E8E4DF] rounded-sm transition-colors"
          >
            <X className="w-4 h-4 text-[#6F6F6F]" />
          </button>
        </div>
        
        {/* Opportunity Strength Badge */}
        <div 
          className="flex items-center gap-2 px-3 py-2 rounded-sm"
          style={{ backgroundColor: strength.bgColor, border: `1px solid ${strength.color}30` }}
        >
          <span className="text-base">{strength.icon}</span>
          <span className="text-xs font-semibold" style={{ color: strength.color }}>
            {strength.label}
          </span>
          <span className="text-xs ml-auto" style={{ color: strength.color }}>
            {probability}% probability
          </span>
        </div>
      </div>
      
      {/* Intelligence Data Grid */}
      <div className="px-5 py-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="p-2 rounded-sm bg-white/50">
            <p className="text-[10px] uppercase tracking-wider text-[#6F6F6F] mb-0.5">Signal Type</p>
            <p className="text-xs font-medium text-[#1C1C1C] capitalize truncate">
              {signalTypeLabel}
            </p>
          </div>
          <div className="p-2 rounded-sm bg-white/50">
            <p className="text-[10px] uppercase tracking-wider text-[#6F6F6F] mb-0.5">Property Type</p>
            <p className="text-xs font-medium text-[#1C1C1C] capitalize">{propertyType}</p>
          </div>
          <div className="p-2 rounded-sm bg-white/50">
            <p className="text-[10px] uppercase tracking-wider text-[#6F6F6F] mb-0.5">Est. Staff Size</p>
            <p className="text-xs font-medium text-[#1C1C1C]">{estimatedStaff} employees</p>
          </div>
          <div className="p-2 rounded-sm bg-white/50">
            <p className="text-[10px] uppercase tracking-wider text-[#6F6F6F] mb-0.5">Probability</p>
            <p className="text-xs font-medium text-[#1C1C1C]">{probability}%</p>
          </div>
        </div>
        
        {/* Opportunity Value Highlight */}
        <div 
          className="p-4 rounded-sm text-center"
          style={{ backgroundColor: '#B89B5E10', border: '1px solid #B89B5E30' }}
        >
          <p className="text-[10px] uppercase tracking-wider text-[#B89B5E] mb-1">
            Est. Uniform Opportunity
          </p>
          <p 
            className="text-2xl font-semibold"
            style={{ color: '#B89B5E', fontFamily: "'Playfair Display', serif" }}
          >
            ${estimatedValue.toLocaleString()}
          </p>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="px-5 py-4 border-t border-[#E8E4DF] space-y-2">
        {marker.type === 'opportunity' || marker.type === 'closed' ? (
          <Button 
            onClick={onViewOpportunity}
            className="w-full h-10 rounded-sm text-xs uppercase tracking-wider"
            style={{ backgroundColor: '#1C1C1C' }}
          >
            <Eye className="w-4 h-4 mr-2" />
            View Opportunity
          </Button>
        ) : (
          <>
            {/* Create Prospect */}
            <Button 
              onClick={handleCreateProspect}
              disabled={actionLoading === 'prospect'}
              className="w-full h-10 rounded-sm text-xs uppercase tracking-wider"
              style={{ backgroundColor: '#B89B5E' }}
            >
              {actionLoading === 'prospect' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Create Prospect
            </Button>
            
            {/* Find Decision Maker */}
            <Button 
              onClick={handleFindDecisionMaker}
              disabled={actionLoading === 'decision-maker'}
              variant="outline"
              className="w-full h-10 rounded-sm text-xs uppercase tracking-wider border-[#1C1C1C] text-[#1C1C1C] hover:bg-[#1C1C1C] hover:text-white"
            >
              {actionLoading === 'decision-maker' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Find Decision Maker
            </Button>
            
            {/* Generate Vision Deck */}
            <Button 
              onClick={handleGenerateVisionDeck}
              disabled={actionLoading === 'vision-deck'}
              variant="outline"
              className="w-full h-10 rounded-sm text-xs uppercase tracking-wider border-[#B89B5E] text-[#B89B5E] hover:bg-[#B89B5E] hover:text-white"
            >
              {actionLoading === 'vision-deck' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate Vision Deck
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// Mobile opportunity list card
function MobileOpportunityCard({ marker, isActive, onClick }) {
  const data = marker.data;
  const estimatedValue = data?.value || data?.deal_value || estimateOpportunityValue(data);
  const signalType = data?.signal_type 
    ? (SIGNAL_PROMPTS[data.signal_type] || data.signal_type).replace(/_/g, ' ')
    : 'Opportunity';
  
  return (
    <div 
      onClick={onClick}
      className={`p-4 border-b border-[#E8E4DF] cursor-pointer transition-all duration-200 ${
        isActive ? 'bg-[#B89B5E10]' : 'bg-white hover:bg-[#F7F5F2]'
      }`}
      style={{ borderLeft: isActive ? '3px solid #B89B5E' : '3px solid transparent' }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 
            className="font-medium text-sm mb-1"
            style={{ color: '#1C1C1C', fontFamily: "'Playfair Display', serif" }}
          >
            {data?.company_name || 'Unknown'}
          </h4>
          <p className="text-xs text-[#6F6F6F] mb-2">
            {data?.city || 'Unknown City'}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-[#6F6F6F]">
            Signal: {signalType.length > 20 ? signalType.substring(0, 20) + '...' : signalType}
          </p>
        </div>
        <div className="text-right">
          <p 
            className="text-sm font-semibold"
            style={{ color: '#B89B5E' }}
          >
            ${(estimatedValue / 1000).toFixed(0)}K
          </p>
          <p className="text-[10px] text-[#6F6F6F] mt-1">Opportunity</p>
        </div>
      </div>
    </div>
  );
}

// Simple clustering function
function clusterMarkers(markers, zoom) {
  if (zoom >= 10 || markers.length < 10) return { clusters: [], individual: markers };
  
  const gridSize = zoom < 6 ? 2 : zoom < 8 ? 1 : 0.5;
  const clustered = {};
  const individual = [];
  
  markers.forEach(marker => {
    const gridLat = Math.floor(marker.position.lat / gridSize) * gridSize;
    const gridLng = Math.floor(marker.position.lng / gridSize) * gridSize;
    const key = `${gridLat}_${gridLng}`;
    
    if (!clustered[key]) {
      clustered[key] = { markers: [], center: { lat: 0, lng: 0 }, totalValue: 0 };
    }
    clustered[key].markers.push(marker);
    clustered[key].center.lat += marker.position.lat;
    clustered[key].center.lng += marker.position.lng;
    clustered[key].totalValue += marker.data?.value || estimateOpportunityValue(marker.data);
  });
  
  const clusters = [];
  Object.values(clustered).forEach(cluster => {
    if (cluster.markers.length > 2) {
      cluster.center.lat /= cluster.markers.length;
      cluster.center.lng /= cluster.markers.length;
      clusters.push(cluster);
    } else {
      individual.push(...cluster.markers);
    }
  });
  
  return { clusters, individual };
}

// Map controller component
function MapController({ onZoomChange }) {
  const map = useMap();
  
  useEffect(() => {
    if (!map) return;
    
    const listener = map.addListener('zoom_changed', () => {
      onZoomChange(map.getZoom());
    });
    
    return () => {
      if (listener) listener.remove();
    };
  }, [map, onZoomChange]);
  
  return null;
}

// ===================== LUXURY MAP TAB =====================

function MapTab({ token, viewingAsUser }) {
  const api = useApi();
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [zoom, setZoom] = useState(6);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleLayers, setVisibleLayers] = useState({
    prospects: true,
    signals: true,
    opportunities: true,
    closed: true
  });

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(function() { fetchMapData(); }, [viewingAsUser?.id]);

  async function fetchMapData() {
    setLoading(true);
    try {
      const data = await api.get('/market-intelligence/map-data');
      setMapData(data);
    } catch (err) {
      console.error('Error fetching map data:', err);
    } finally {
      setLoading(false);
    }
  }

  function toggleLayer(layer) {
    setVisibleLayers(prev => ({ ...prev, [layer]: !prev[layer] }));
  }

  // Check if a signal is "new" (created in last 24 hours)
  function isRecentSignal(data) {
    if (!data?.created_at) return false;
    const createdAt = new Date(data.created_at);
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return createdAt > oneDayAgo;
  }

  function getAllMarkers() {
    if (!mapData) return [];
    const markers = [];
    
    if (visibleLayers.prospects) {
      (mapData.prospects || []).forEach(p => {
        if (p.geo?.lat && p.geo?.lng) {
          markers.push({ 
            type: 'prospect', 
            data: p, 
            position: { lat: p.geo.lat, lng: p.geo.lng },
            isNew: isRecentSignal(p)
          });
        }
      });
    }
    if (visibleLayers.signals) {
      (mapData.signals || []).forEach(s => {
        if (s.geo?.lat && s.geo?.lng) {
          markers.push({ 
            type: 'signal', 
            data: s, 
            position: { lat: s.geo.lat, lng: s.geo.lng },
            isNew: isRecentSignal(s)
          });
        }
      });
    }
    if (visibleLayers.opportunities) {
      (mapData.opportunities || []).forEach(o => {
        if (o.geo?.lat && o.geo?.lng) {
          markers.push({ 
            type: 'opportunity', 
            data: o, 
            position: { lat: o.geo.lat, lng: o.geo.lng },
            isNew: isRecentSignal(o)
          });
        }
      });
    }
    if (visibleLayers.closed) {
      (mapData.closed_clients || []).forEach(c => {
        if (c.geo?.lat && c.geo?.lng) {
          markers.push({ 
            type: 'closed', 
            data: c, 
            position: { lat: c.geo.lat, lng: c.geo.lng },
            isNew: false
          });
        }
      });
    }
    return markers;
  }

  const allMarkers = getAllMarkers();
  const defaultCenter = { lat: 56.13, lng: -106.35 }; // Canada center
  const center = allMarkers.length > 0 ? allMarkers[0].position : defaultCenter;
  
  // Calculate clusters
  const { clusters, individual } = clusterMarkers(allMarkers, zoom);

  const counts = {
    prospects: mapData?.prospects?.length || 0,
    signals: mapData?.signals?.length || 0,
    opportunities: mapData?.opportunities?.length || 0,
    closed: mapData?.closed_clients?.length || 0
  };

  const totalMarkers = counts.prospects + counts.signals + counts.opportunities + counts.closed;
  
  // Calculate total pipeline value
  const totalPipelineValue = allMarkers.reduce((sum, m) => {
    return sum + (m.data?.value || m.data?.deal_value || estimateOpportunityValue(m.data));
  }, 0);

  const handleMarkerClick = (marker, key) => {
    setSelectedMarker({ key, ...marker });
  };

  const handleClusterClick = (cluster) => {
    // Zoom in to show cluster details
    setZoom(prev => Math.min(prev + 3, 14));
  };

  // Count new signals (last 24 hours)
  const newSignalsCount = allMarkers.filter(m => m.isNew).length;

  return (
    <div className="space-y-6" data-testid="luxury-map-tab">
      {/* Map Header */}
      <div className="pb-6 border-b border-border">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-serif text-2xl text-[#1C1C1C]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Market Opportunities Map
            </h2>
            <p className="text-sm text-[#6F6F6F] mt-1">
              Live development signals across Canada
            </p>
          </div>
        </div>
        
        {/* Insight Summary */}
        <div className="flex items-center gap-4 sm:gap-6 p-4 rounded-sm flex-wrap" style={{ backgroundColor: '#F7F5F2', border: '1px solid #E8E4DF' }}>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#B89B5E] animate-pulse" />
            <span className="text-xs uppercase tracking-wider text-[#6F6F6F]">Opportunities</span>
            <span className="font-semibold text-[#1C1C1C] ml-1">{totalMarkers}</span>
          </div>
          <div className="w-px h-4 bg-[#E8E4DF] hidden sm:block" />
          <div className="flex items-center gap-2">
            <DollarSign className="w-3 h-3 text-[#B89B5E]" />
            <span className="text-xs uppercase tracking-wider text-[#6F6F6F]">Pipeline</span>
            <span className="font-semibold text-[#B89B5E] ml-1">
              ${totalPipelineValue >= 1000000 
                ? `${(totalPipelineValue / 1000000).toFixed(1)}M` 
                : `${Math.round(totalPipelineValue / 1000)}K`}
            </span>
          </div>
          {newSignalsCount > 0 && (
            <>
              <div className="w-px h-4 bg-[#E8E4DF] hidden sm:block" />
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Zap className="w-3 h-3 text-[#B89B5E]" />
                  <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-[#B89B5E] rounded-full animate-ping" />
                </div>
                <span className="text-xs uppercase tracking-wider text-[#6F6F6F]">New (24h)</span>
                <span className="font-semibold text-[#B89B5E] ml-1">{newSignalsCount}</span>
              </div>
            </>
          )}
        </div>
      </div>

      <Card className="border-[#E8E4DF] rounded-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-[#F7F5F2]">
              <Loader2 className="w-8 h-8 animate-spin text-[#B89B5E] mb-3" />
              <p className="text-sm text-[#6F6F6F]">Loading market intelligence...</p>
            </div>
          ) : (
            <div className={isMobile ? "flex flex-col" : ""}>
              {/* Layer Toggle Controls */}
              <div className="flex items-center gap-3 p-4 flex-wrap bg-white border-b border-[#E8E4DF]">
                <span className="text-xs uppercase tracking-wider text-[#6F6F6F] mr-2">Layers:</span>
                <button
                  onClick={() => toggleLayer('signals')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider transition-all border ${
                    visibleLayers.signals 
                      ? 'bg-[#B89B5E10] text-[#B89B5E] border-[#B89B5E]' 
                      : 'bg-white text-[#6F6F6F] border-[#E8E4DF]'
                  }`}
                >
                  <span 
                    className="w-2.5 h-2.5 rounded-full" 
                    style={{ backgroundColor: visibleLayers.signals ? LUXURY_MARKER_COLORS.hotel : '#D1D5DB' }}
                  />
                  Signals ({counts.signals})
                </button>
                <button
                  onClick={() => toggleLayer('opportunities')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider transition-all border ${
                    visibleLayers.opportunities 
                      ? 'bg-[#B89B5E10] text-[#B89B5E] border-[#B89B5E]' 
                      : 'bg-white text-[#6F6F6F] border-[#E8E4DF]'
                  }`}
                >
                  <span 
                    className="w-2.5 h-2.5 rounded-full" 
                    style={{ backgroundColor: visibleLayers.opportunities ? LUXURY_MARKER_COLORS.opportunity : '#D1D5DB' }}
                  />
                  Active ({counts.opportunities})
                </button>
                <button
                  onClick={() => toggleLayer('prospects')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider transition-all border ${
                    visibleLayers.prospects 
                      ? 'bg-[#6F6F6F10] text-[#6F6F6F] border-[#6F6F6F]' 
                      : 'bg-white text-[#9CA3AF] border-[#E8E4DF]'
                  }`}
                >
                  <span 
                    className="w-2.5 h-2.5 rounded-full" 
                    style={{ backgroundColor: visibleLayers.prospects ? LUXURY_MARKER_COLORS.prospect : '#D1D5DB' }}
                  />
                  Prospects ({counts.prospects})
                </button>
                <button
                  onClick={() => toggleLayer('closed')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs uppercase tracking-wider transition-all border ${
                    visibleLayers.closed 
                      ? 'bg-[#2D501610] text-[#2D5016] border-[#2D5016]' 
                      : 'bg-white text-[#9CA3AF] border-[#E8E4DF]'
                  }`}
                >
                  <span 
                    className="w-2.5 h-2.5 rounded-full" 
                    style={{ backgroundColor: visibleLayers.closed ? LUXURY_MARKER_COLORS.closed : '#D1D5DB' }}
                  />
                  Won ({counts.closed})
                </button>
              </div>

              {totalMarkers === 0 ? (
                <div className="text-center py-20 bg-[#F7F5F2]">
                  <div 
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: '#B89B5E20' }}
                  >
                    <Map className="w-8 h-8 text-[#B89B5E]" />
                  </div>
                  <p className="font-serif text-lg text-[#1C1C1C] mb-1">No Locations Detected</p>
                  <p className="text-sm text-[#6F6F6F]">Add prospects or signals with location data to populate the map</p>
                </div>
              ) : !GOOGLE_MAPS_API_KEY ? (
                <div className="text-center py-20 bg-[#F7F5F2]">
                  <div 
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: '#B89B5E20' }}
                  >
                    <Map className="w-8 h-8 text-[#B89B5E]" />
                  </div>
                  <p className="font-serif text-lg text-[#1C1C1C] mb-1">{totalMarkers} Opportunities Ready</p>
                  <p className="text-sm text-[#6F6F6F]">Google Maps API key required for visualization</p>
                </div>
              ) : (
                <div className={isMobile ? "flex flex-col" : ""}>
                  {/* Map Container */}
                  <div 
                    className="relative" 
                    style={{ height: isMobile ? '50vh' : '600px' }}
                  >
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <GoogleMap
                        defaultCenter={center}
                        defaultZoom={5}
                        mapId="luxury-opportunity-map"
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                        style={{ width: '100%', height: '100%' }}
                      >
                        <MapController onZoomChange={setZoom} />
                        
                        {/* Render clusters */}
                        {clusters.map((cluster, idx) => (
                          <ClusterMarker
                            key={`cluster-${idx}`}
                            position={cluster.center}
                            count={cluster.markers.length}
                            totalValue={cluster.totalValue}
                            onClick={() => handleClusterClick(cluster)}
                          />
                        ))}
                        
                        {/* Render individual markers */}
                        {individual.map((marker, idx) => {
                          const key = `${marker.type}-${marker.data.id || idx}`;
                          return (
                            <LuxuryMarker
                              key={key}
                              position={marker.position}
                              type={marker.type}
                              data={marker.data}
                              isSelected={selectedMarker?.key === key}
                              isHovered={hoveredMarker === key}
                              isNew={marker.isNew}
                              onClick={() => handleMarkerClick(marker, key)}
                            />
                          );
                        })}

                        {/* Intelligence Card Popup — desktop only.
                         * Mobile renders the same card inside a bottom-sheet
                         * Drawer below the map so action buttons are always
                         * reachable and not covered by the legend overlay. */}
                        {selectedMarker && !isMobile && (
                          <InfoWindow 
                            position={selectedMarker.position}
                            onCloseClick={() => setSelectedMarker(null)}
                            pixelOffset={[0, -20]}
                          >
                            <IntelligenceCard
                              marker={selectedMarker}
                              token={token}
                              onClose={() => setSelectedMarker(null)}
                              onViewOpportunity={() => {
                                if (selectedMarker.data?.opportunity_id) {
                                  window.location.href = `/opportunity/${selectedMarker.data.opportunity_id}`;
                                } else if (selectedMarker.data?.id && selectedMarker.type === 'opportunity') {
                                  window.location.href = `/opportunity/${selectedMarker.data.id}`;
                                }
                              }}
                              onCreateDeal={() => {
                                toast.info('Converting to opportunity...');
                                setSelectedMarker(null);
                              }}
                              onAddNote={() => {
                                toast.info('Note feature coming soon');
                              }}
                            />
                          </InfoWindow>
                        )}
                      </GoogleMap>
                    </APIProvider>

                    {/* Mobile bottom-sheet — replaces the cramped InfoWindow
                     * on small screens so action buttons are always reachable. */}
                    {isMobile && (
                      <Drawer
                        open={!!selectedMarker}
                        onOpenChange={(open) => { if (!open) setSelectedMarker(null); }}
                      >
                        <DrawerContent
                          className="max-h-[90vh]"
                          data-testid="opportunity-map-mobile-sheet"
                        >
                          <DrawerHeader className="sr-only">
                            <DrawerTitle>Opportunity details</DrawerTitle>
                          </DrawerHeader>
                          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-1 pb-8" data-testid="opportunity-map-mobile-sheet-scroll">
                            {selectedMarker && (
                              <IntelligenceCard
                                marker={selectedMarker}
                                token={token}
                                onClose={() => setSelectedMarker(null)}
                                onViewOpportunity={() => {
                                  if (selectedMarker.data?.opportunity_id) {
                                    window.location.href = `/opportunity/${selectedMarker.data.opportunity_id}`;
                                  } else if (selectedMarker.data?.id && selectedMarker.type === 'opportunity') {
                                    window.location.href = `/opportunity/${selectedMarker.data.id}`;
                                  }
                                }}
                                onCreateDeal={() => {
                                  toast.info('Converting to opportunity...');
                                  setSelectedMarker(null);
                                }}
                                onAddNote={() => {
                                  toast.info('Note feature coming soon');
                                }}
                              />
                            )}
                          </div>
                        </DrawerContent>
                      </Drawer>
                    )}

                    {/* Legend overlay — desktop only (it covered the click
                     * card on mobile). */}
                    {!isMobile && (
                    <div 
                      className="absolute bottom-4 left-4 p-3 rounded-sm"
                      style={{ 
                        backgroundColor: 'rgba(247, 245, 242, 0.95)',
                        border: '1px solid #E8E4DF',
                        backdropFilter: 'blur(4px)'
                      }}
                    >
                      <p className="text-[10px] uppercase tracking-wider text-[#6F6F6F] mb-2">Marker Legend</p>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: LUXURY_MARKER_COLORS.hotel }} />
                          <span className="text-xs text-[#1C1C1C]">Hotel Opportunity</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: LUXURY_MARKER_COLORS.renovation }} />
                          <span className="text-xs text-[#1C1C1C]">Renovation Signal</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: LUXURY_MARKER_COLORS.restaurant }} />
                          <span className="text-xs text-[#1C1C1C]">Restaurant</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: LUXURY_MARKER_COLORS.closed }} />
                          <span className="text-xs text-[#1C1C1C]">Closed Won</span>
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                  
                  {/* Mobile Opportunity List */}
                  {isMobile && (
                    <div 
                      className="border-t border-[#E8E4DF]"
                      style={{ maxHeight: '40vh', overflowY: 'auto' }}
                    >
                      <div className="sticky top-0 bg-white px-4 py-2 border-b border-[#E8E4DF]">
                        <p className="text-[10px] uppercase tracking-wider text-[#6F6F6F]">
                          Nearby Opportunities ({allMarkers.length})
                        </p>
                      </div>
                      {allMarkers.slice(0, 20).map((marker, idx) => {
                        const key = `${marker.type}-${marker.data.id || idx}`;
                        return (
                          <MobileOpportunityCard
                            key={key}
                            marker={marker}
                            isActive={selectedMarker?.key === key}
                            onClick={() => handleMarkerClick(marker, key)}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ===================== TERRITORY INSIGHTS TAB =====================

function TerritoryTab({ token, viewingAsUser }) {
  const api = useApi();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(function() { fetchInsights(); }, [viewingAsUser?.id]);

  async function fetchInsights() {
    setLoading(true);
    try {
      const data = await api.get('/market-intelligence/territory-insights');
      setInsights(data);
    } catch (err) {
      console.error('Error fetching insights:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" /></div>;
  }

  const totals = insights?.totals || {};
  const values = insights?.values || {};
  const signalBreakdown = insights?.signal_breakdown || {};
  const industryBreakdown = insights?.industry_breakdown || {};

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-[#1A1A1A]">Territory Insights</h2>
        <p className="text-sm text-[#6B7280]">High-level market penetration metrics</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4 text-center">
            <Building2 className="w-8 h-8 text-[#C9A227] mx-auto mb-2" />
            <p className="text-2xl font-semibold text-[#1A1A1A]">{totals.accounts || 0}</p>
            <p className="text-sm text-[#6B7280]">Total Accounts</p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4 text-center">
            <Target className="w-8 h-8 text-gray-500 mx-auto mb-2" />
            <p className="text-2xl font-semibold text-[#1A1A1A]">{totals.prospects || 0}</p>
            <p className="text-sm text-[#6B7280]">Prospects</p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4 text-center">
            <Radar className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-semibold text-[#1A1A1A]">{totals.active_signals || 0}</p>
            <p className="text-sm text-[#6B7280]">Active Signals</p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4 text-center">
            <Briefcase className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-semibold text-[#1A1A1A]">{totals.active_opportunities || 0}</p>
            <p className="text-sm text-[#6B7280]">Active Opportunities</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="border-[#E5E7EB]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Pipeline Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B7280]">Active Pipeline</span>
                <span className="text-lg font-semibold text-[#1A1A1A]">${(values.pipeline_value || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B7280]">Closed Won</span>
                <span className="text-lg font-semibold text-green-600">${(values.won_value || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-sm text-[#6B7280]">Win Rate</span>
                <span className="text-lg font-semibold text-[#1A1A1A]">
                  {totals.closed_won + totals.closed_lost > 0 
                    ? Math.round((totals.closed_won / (totals.closed_won + totals.closed_lost)) * 100) 
                    : 0}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E5E7EB]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Signal Types</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(signalBreakdown).length === 0 ? (
              <p className="text-sm text-[#9CA3AF] text-center py-4">No signals logged yet</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(signalBreakdown).map(function([type, count]) {
                  return (
                    <div key={type} className="flex items-center justify-between">
                      <span className="text-sm text-[#6B7280]">{SIGNAL_PROMPTS[type]?.split(' ').slice(0, 3).join(' ') || type}</span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-[#E5E7EB]">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Industry Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(industryBreakdown).length === 0 ? (
            <p className="text-sm text-[#9CA3AF] text-center py-4">No prospects by industry yet</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {Object.entries(industryBreakdown).map(function([industry, count]) {
                return (
                  <div key={industry} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-[#1A1A1A]">{industry}</span>
                    <Badge className="bg-[#C9A227] text-white">{count}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ===================== INDUSTRY-AWARE RADAR SUBTITLE =====================
// Mirrors the Tier-1 classifier in backend `services/sweep_query_builder.py`.
// Reads seller_industry + value_proposition (the "what I sell" fields) and
// returns the matched industry id. Used ONLY to pick the radar subtitle —
// the authoritative classifier still runs server-side during sweeps.
// Order matches the backend: first positive hit within Tier-1 wins.
const _RADAR_INDUSTRY_KEYWORDS = [
  ['hospitality', [
    'hotel', 'hotels', 'resort', 'resorts', 'hospitality', 'restaurant',
    'restaurants', 'f&b', 'food and beverage', 'boutique hotel',
    'lodging', 'inn', 'motel', 'guest house', 'hospitality design',
    'guest room', 'guest rooms',
  ]],
  ['corporate_services', [
    'catering', 'corporate catering', 'event catering', 'food service',
    'food services', 'boardroom', 'cleaning', 'janitorial',
    'commercial cleaning', 'facility services', 'facility management',
    'facilities', 'security services', 'guard service', 'av services',
    'audio visual', 'it services', 'managed services', 'office services',
    'courier', 'mail room', 'concierge service', 'uniform service',
    'linens', 'promotional products',
  ]],
  ['design_build', [
    'interior design', 'interior designer', 'architecture', 'architect',
    'architectural', 'general contractor', 'construction', 'build out',
    'millwork', 'fit out', 'fit-out', 'aec', 'furniture design',
    'commercial interior', 'workplace design', 'hospitality design',
  ]],
  ['sales_tools', [
    'sales tools', 'sales technology', 'sales crm',
    'crm software', 'sales enablement',
    'revenue operations', 'revops',
    'sales intelligence', 'b2b sales',
    'sales consulting', 'sales training',
    'sales coaching', 'bdr', 'sdr',
    'account executive', 'sales development',
    'sales team', 'sales hiring', 'sales stack',
    'outbound sales',
  ]],
];

// Catering vs cleaning sub-classifier (mirrors backend
// `_refine_corporate_services`). Fires after corporate_services matches.
const _CATERING_KW = [
  'catering', 'corporate catering', 'event catering', 'food service',
  'food services', 'boardroom catering', 'office lunch',
  'office breakfast', 'office food',
];
const _CLEANING_KW = [
  'cleaning', 'janitorial', 'commercial cleaning', 'facility services',
  'facility management', 'facilities', 'facilities management',
  'janitor', 'post-construction clean', 'office cleaning',
];

function classifyRadarIndustry(profile) {
  // Tier-1: seller_industry + value_proposition only ("what I sell").
  // Falls back to "" if profile is null/empty — caller then defaults the
  // subtitle to the generic "Canadian Market Intelligence Radar".
  if (!profile) return '';
  const bag = [
    profile.seller_industry || '',
    profile.value_proposition || '',
  ].join(' ').toLowerCase();
  if (!bag.trim()) return '';
  for (const [industryId, kws] of _RADAR_INDUSTRY_KEYWORDS) {
    if (kws.some((kw) => bag.includes(kw))) {
      if (industryId === 'corporate_services') {
        const cateringHits = _CATERING_KW.filter((k) => bag.includes(k)).length;
        const cleaningHits = _CLEANING_KW.filter((k) => bag.includes(k)).length;
        if (cateringHits > cleaningHits && cateringHits > 0) return 'catering';
        if (cleaningHits > cateringHits && cleaningHits > 0) return 'cleaning';
        return 'corporate_services';
      }
      return industryId;
    }
  }
  return 'generic_b2b';
}

// Map classified industry → human-facing radar subtitle.
const _RADAR_SUBTITLES = {
  sales_tools:        'Canadian Sales Intelligence Radar',
  hospitality:        'Canadian Hospitality Opportunity Radar',
  catering:           'Canadian Catering Opportunity Radar',
  cleaning:           'Canadian Facilities Opportunity Radar',
  design_build:       'Canadian Design & Build Radar',
  corporate_services: 'Canadian B2B Opportunity Radar',
  generic_b2b:        'Canadian B2B Opportunity Radar',
};
const _RADAR_SUBTITLE_DEFAULT = 'Canadian Market Intelligence Radar';

function radarSubtitleFor({ multiIndustryMode, classifiedIndustry }) {
  if (multiIndustryMode) return _RADAR_SUBTITLE_DEFAULT;
  return _RADAR_SUBTITLES[classifiedIndustry] || _RADAR_SUBTITLE_DEFAULT;
}

// ───────────────────── Signal → Decision Maker (Feb 2026) ─────────────────────
// Two helpers used by the "Find Decision Maker" button on confirmed signal
// cards in the radar. Both are pure functions of the signal + seller
// profile so they're trivially testable and have no side effects.

// Extract the company name from a signal. Apollo / SerpAPI / news pipelines
// stamp `company_name` directly on the signal during persistence; older
// signals may only have it inside `extracted_company_name`. Last-ditch
// fallback: parse the first capitalized phrase from the title (handles
// "Salesforce hiring BDR Toronto" → "Salesforce").
export function extractSignalCompany(signal) {
  if (!signal) return '';
  const direct = signal.company_name
    || signal.extracted_company_name
    || (signal.entities_found && signal.entities_found[0])
    || '';
  if (direct && typeof direct === 'string') return direct.trim();

  const title = (signal.title || '').trim();
  if (!title) return '';
  // Common patterns: "Salesforce hiring BDR Toronto",
  // "Cintas appoints VP Sales", "ACME Corp announces..."
  // Strip leading quoting / dashes, then take the prefix before a
  // hiring/job verb or location separator.
  const cleaned = title.replace(/^["'\u2013\u2014-]\s*/, '');
  const stop = /\s+(hiring|hires|appoints?|announces?|opens?|expand(?:s|ing)?|launches?|to hire|jobs?|seeking|seeks|named|promotes?|wants|posts? a|posts new|grows?|grew|raises?|appointed|adds?)\b/i;
  const m = cleaned.split(stop);
  return (m[0] || cleaned).trim().replace(/[,:;].*$/, '').trim();
}

// Decide which job title to search for given a signal type. Mirrors the
// "who BUILDS the team, not who's being hired" insight: when a company is
// hiring sales reps, we want the sales LEADER (VP Sales / Director of
// Sales / Head of Sales), not the BDR being recruited. For non-hiring
// signals, fall back to the first buyer_role from the seller's Industry
// Architect (seller_context.buyer_roles).
export function determineDmJobTitle(signal, sellerProfile) {
  const sigType = (signal && signal.signal_type || '').toLowerCase();
  const HIRING_TYPES = new Set([
    'hiring_signal', 'hiring_surge', 'sales_hiring',
    'hiring', 'team_growth', 'recruiting',
  ]);
  if (HIRING_TYPES.has(sigType)) {
    // The person BUILDING the team. Apollo accepts an OR list via
    // job_titles[]; we send the first one to keep the search tight.
    return 'VP Sales';
  }
  // Non-hiring → first buyer_role from Industry Architect.
  const roles = sellerProfile && sellerProfile.buyer_roles;
  if (Array.isArray(roles) && roles.length > 0) {
    const first = roles[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && first.role) return first.role;
  }
  // Last-ditch fallback so the button is always actionable.
  return 'General Manager';
}

// ===================== MAIN COMPONENT =====================

export default function MarketIntelligence() {
  const { token, user, viewingAsUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'radar';
  const highlightedSignalId = searchParams.get('signal');
  const api = useApi();

  // Feb 2026 — dynamic radar subtitle. Fetches sweep-settings (for
  // multi_industry_mode) and seller-context (for classifying the
  // seller_industry / value_proposition). Defaults to "Canadian Market
  // Intelligence Radar" if either fetch fails — never blocks the page.
  const [radarSubtitle, setRadarSubtitle] = useState(
    'Canadian Market Intelligence Radar'
  );
  // Feb 2026 — seller profile is loaded once at the page level and passed
  // into RadarTab so the per-card "Find Decision Maker" button can look
  // up the seller's Industry Architect `buyer_roles` for non-hiring signals.
  const [sellerProfile, setSellerProfile] = useState(null);

  React.useEffect(() => {
    let cancelled = false;
    async function loadRadarSubtitle() {
      try {
        const [sweep, sellerCtx] = await Promise.all([
          api.get('/intelligence/sweep-settings').catch(() => null),
          api.get('/sales-intelligence/seller-context').catch(() => null),
        ]);
        if (cancelled) return;
        const multiIndustryMode = !!(sweep && sweep.multi_industry_mode);
        const profile = (sellerCtx && sellerCtx.profile) || null;
        setSellerProfile(profile);
        const classifiedIndustry = classifyRadarIndustry(profile);
        setRadarSubtitle(radarSubtitleFor({
          multiIndustryMode, classifiedIndustry,
        }));
      } catch {
        // Default subtitle already set — fail silent.
      }
    }
    loadRadarSubtitle();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleTabChange(value) {
    // Preserve signal param when switching tabs
    const newParams = { tab: value };
    if (highlightedSignalId) {
      newParams.signal = highlightedSignalId;
    }
    setSearchParams(newParams);
  }

  // If there's a signal to highlight, ensure we're on the radar tab
  React.useEffect(() => {
    if (highlightedSignalId && activeTab !== 'radar') {
      setSearchParams({ tab: 'radar', signal: highlightedSignalId });
    }
  }, [highlightedSignalId, activeTab, setSearchParams]);

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="market-intelligence-page">
        {/* Header */}
        <div className="flex items-end justify-between pb-6 border-b border-border">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
              Strategic Intelligence
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl">Market Signals</h1>
            <p className="text-muted-foreground mt-2" data-testid="radar-subtitle">{radarSubtitle}</p>
          </div>
          {user?.role === 'admin' && (
            <Link to="/intelligence-settings">
              <Button variant="outline" className="rounded-sm gap-2">
                <Settings className="w-4 h-4" />
                Intelligence Settings
              </Button>
            </Link>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="bg-muted/50 rounded-sm p-1 mb-6">
            <TabsTrigger value="radar" className="data-[state=active]:bg-card data-[state=active]:shadow-none rounded-sm flex items-center gap-2">
              <Radar className="w-4 h-4" />Radar
            </TabsTrigger>
            <TabsTrigger value="style-detection" className="data-[state=active]:bg-card data-[state=active]:shadow-none rounded-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4" />Style Detection
            </TabsTrigger>
            <TabsTrigger value="prospects" className="data-[state=active]:bg-card data-[state=active]:shadow-none rounded-sm flex items-center gap-2">
              <Target className="w-4 h-4" />Prospects
            </TabsTrigger>
            <TabsTrigger value="map" className="data-[state=active]:bg-card data-[state=active]:shadow-none rounded-sm flex items-center gap-2">
              <Map className="w-4 h-4" />Opportunity Map
            </TabsTrigger>
            <TabsTrigger value="territory" className="data-[state=active]:bg-card data-[state=active]:shadow-none rounded-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />Territory Insights
            </TabsTrigger>
          </TabsList>

          <TabsContent value="radar"><RadarTab token={token} highlightedSignalId={highlightedSignalId} viewingAsUser={viewingAsUser} sellerProfile={sellerProfile} /></TabsContent>
          <TabsContent value="style-detection"><StyleDetectionTab token={token} /></TabsContent>
          <TabsContent value="prospects"><ProspectsTab token={token} viewingAsUser={viewingAsUser} /></TabsContent>
          <TabsContent value="map"><MapTab token={token} viewingAsUser={viewingAsUser} /></TabsContent>
          <TabsContent value="territory"><TerritoryTab token={token} viewingAsUser={viewingAsUser} /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
